import { Fragment, useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  ticketsAPI, capsulesAPI, capsuleConfigAPI, formatApiError, logsAPI, teamAPI,
  httpTurnClientTimeoutMs, DEFAULT_HTTP_TURN_BUDGET_SEC,
} from '@/lib/api';
import ArtifactDialog from '@/components/tickets/ArtifactDialog';
import ActividadAgentica from '@/components/ActividadAgentica';
import ContactPicker from '@/components/tickets/ContactPicker';
import CambiarSolicitanteDialog from '@/components/tickets/CambiarSolicitanteDialog';
import LabelMultiSelect from '@/components/tickets/LabelMultiSelect';
import TicketBoard from '@/components/tickets/TicketBoard';
import MemoryEventPanel from '@/components/tickets/MemoryEventPanel';
import CasosParecidos from '@/components/tickets/CasosParecidos';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import {
  Plus, FileText, ChevronRight, Search, Star,
  MessageSquare, Send, Bot, User, Shield, Loader2,
  AlertTriangle, Clock, Zap, Sparkles, X, RefreshCw, Database,
  Phone, Monitor, Mail, Reply, Trash2, ClipboardList, Activity, Save, FolderTree,
  LayoutGrid, Building2, GitBranch, ExternalLink, Pencil, BookOpen, UserX,
} from 'lucide-react';
import { toast } from 'sonner';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import PedirLaResolucion from '@/components/PedirLaResolucion';
import EmailMessageContext, { formatDateTime } from '@/components/EmailMessageContext';
import EmailMessageBody from '@/components/EmailMessageBody';
import { MessageAttachments } from '@/components/MessageAttachments';
import { fechaYHora, soloHora, soloFecha } from '../lib/fechas';
import MoverElCompromiso from '@/components/tickets/MoverElCompromiso';
import FechaComprometida, { FechaComprometidaCorta } from '@/components/FechaComprometida';

const priorityColors = {
  low: 'bg-zinc-100 text-zinc-700',
  medium: 'bg-blue-50 text-brand-primary',
  high: 'bg-orange-50 text-orange-700',
  critical: 'bg-red-50 text-brand-danger',
};

function slugifyIntent(value) {
  return (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function findTemplateForIntent(templates, intentId) {
  if (!intentId || !templates?.length) return null;
  const norm = slugifyIntent(intentId);
  return templates.find((t) => (t.intents || []).some((i) => slugifyIntent(i) === norm)) || null;
}

/**
 * Cómo viene un caso contra la fecha que se le prometió a quien lo pidió.
 *
 * **El color lo decide el backend** (`relojes.semaforo_del_compromiso`) y acá sólo se pinta. Es
 * el mismo semáforo que ve el solicitante en *Mis solicitudes*: dos definiciones de «esto se está
 * por vencer» en el mismo producto es como se termina discutiendo cuál tablero tiene razón.
 *
 * **Sin compromiso no se pinta nada.** Dar por cumplido lo que nadie prometió sería mentir hacia
 * el lado que nadie revisa.
 */
//: Las franjas de la bandeja por prioridad, en el orden en que se muestran (2026-10-09).
const FRANJAS = {
  urgente: 'Urgente — vencido, por vencer o crítico',
  priorizado: 'Prioridad de la empresa',
  resto: 'El resto, por fecha comprometida',
};
const RANGO_DE_FRANJA = { urgente: 0, priorizado: 1, resto: 2 };

function SemaforoDelCaso({ caso }) {
  // Desde el 2026-10-09 es la misma pieza que usan todas las pantallas: la fecha, su estado y
  // la frase de origen salen de `caso.compromiso`. Ver `components/FechaComprometida.jsx`.
  return <span className="text-[10px]"><FechaComprometidaCorta caso={caso} /></span>;
}

/**
 * Este caso espera a alguien que no va a estar antes de lo que se prometió.
 *
 * **Lo pidió Facundo así:** *"el problema es cuando yo no esté, de vacaciones"*. Lo calcula el
 * backend cruzando el calendario del área con la disponibilidad de la persona, **al mirar y sin
 * guardarlo**: una marca persistida queda vieja el día que cambian las vacaciones o se mueve el
 * compromiso, y una bandeja que avisa de un problema que ya no existe se deja de mirar entera.
 *
 * **No se reasigna solo, y no es una limitación: es la decisión.** Quién sigue un caso lo sabe
 * alguien que conoce el equipo, no un calendario. Lo único que hace esto es que no se enteren
 * tarde.
 */
/**
 * Cómo le fue al caso, según quien lo pidió.
 *
 * **Sin encuesta contestada no dibuja nada.** Un "sin datos" en cada ficha sería ruido en el
 * 90 %: la encuesta es opcional, arranca apagada y la mayoría no la contesta. Lo que importa es
 * que cuando **sí** hay respuesta se vea, y que un número bajo se note.
 *
 * Se guardaba en el caso desde que existe la encuesta y no se mostraba en ninguna pantalla: el
 * número vivía sólo en la mediana del tablero de Dirección, donde no se puede saber **qué caso**
 * salió mal. Quien lo trabajó era el último en enterarse de cómo le fue.
 */
function MarcaDeConformidad({ valor }) {
  const v = valor?.valor;
  if (v == null) return null;
  const escala = valor?.escala || 5;
  // Bajo, medio y alto sobre la escala declarada y no sobre 5: una cápsula puede usar 1-10.
  const ratio = v / escala;
  const tono = ratio <= 0.4
    ? 'bg-brand-danger/10 text-brand-danger border-brand-danger/30'
    : ratio >= 0.8
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : 'bg-amber-50 text-amber-800 border-amber-200';
  return (
    <span
      data-testid="conformidad-del-caso"
      className={`text-[10px] px-1.5 py-0.5 rounded-sm border flex items-center gap-1 ${tono}`}
      title={valor?.comentario ? `"${valor.comentario}"` : 'Sin comentario'}
    >
      <Star className="w-3 h-3" /> {v}/{escala}{valor?.comentario ? ' · con comentario' : ''}
    </span>
  );
}

function EsperaAAlguienQueNoEsta({ caso }) {
  const marca = caso?.espera_a_alguien_que_no_esta;
  if (!marca) return null;
  return (
    <span
      data-testid={`espera-a-alguien-${caso.ticket_id}`}
      className="text-[10px] text-amber-700 flex items-center gap-1"
      title={`${marca.persona}: ${marca.motivo}. Decidí vos si lo pasás a otra persona.`}
    >
      <UserX className="w-2.5 h-2.5" />
      espera a {marca.persona}
    </span>
  );
}

const statusColors = {
  new: 'border-brand-primary text-brand-primary',
  triaging: 'border-brand-warning text-brand-warning',
  in_progress: 'border-brand-success text-brand-success',
  resolved: 'border-zinc-400 text-zinc-400',
  closed: 'border-zinc-300 text-zinc-300',
  escalated_to_dev: 'border-purple-500 text-purple-600',
};

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  return soloHora(d, '');
}

function formatDate(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  return soloFecha(d, '');
}

// ── Ticket Chat Panel ──
function TicketChatPanel({ ticket, tenantId, capsuleId, onArtifactCreated }) {
  const autopsyConvId = `ticket-chat-${ticket.ticket_id}`;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [chatTimeoutMs, setChatTimeoutMs] = useState(httpTurnClientTimeoutMs());
  const [agentInfo, setAgentInfo] = useState({
    agent: '',
    skillsConfigured: [],
    skillsLoaded: [],
    toolsExecuted: [],
    warning: null,
    turnHealth: null,
    transportCode: null,
  });
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await logsAPI.platformTurnBudget();
        const sec = res.data?.http_turn_budget_sec || DEFAULT_HTTP_TURN_BUDGET_SEC;
        if (!cancelled) setChatTimeoutMs(httpTurnClientTimeoutMs(sec));
      } catch {
        /* default */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load persisted chat history on mount / ticket change
  useEffect(() => {
    setAgentInfo({
      agent: '',
      skillsConfigured: [],
      skillsLoaded: [],
      toolsExecuted: [],
      warning: null,
      turnHealth: null,
      transportCode: null,
    });
    loadHistory();
  }, [ticket.ticket_id]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await ticketsAPI.getChatHistory(tenantId, capsuleId, ticket.ticket_id);
      const history = (res.data.messages || []).map(m => ({
        role: m.role,
        content: m.content,
        timestamp: m.timestamp,
        agent_used: m.agent_used,
      }));
      setMessages(history);
    } catch {
      // No history yet, that's fine
    } finally {
      setLoadingHistory(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setAgentInfo((prev) => ({ ...prev, turnHealth: null, transportCode: null, warning: null }));

    try {
      const res = await ticketsAPI.chat(
        tenantId,
        capsuleId,
        ticket.ticket_id,
        {
          message: userMsg.content,
          chat_history: [...messages, userMsg].map(m => ({ role: m.role, content: m.content })),
        },
        { timeoutMs: chatTimeoutMs },
      );
      const data = res.data;
      const skillsConfigured = data.skills_configured || data.skills_active || [];
      const skillsLoaded = data.skills_loaded || [];
      const toolsExecuted = data.tools_executed || [];
      const turnHealth = data.turn_health || null;
      let warning = null;
      if (skillsConfigured.includes('gitlab-integration') && !skillsLoaded.includes('gitlab-integration')) {
        warning = 'GitLab esta habilitada en el agente pero no se cargo en runtime. Revisa Config Capsula → skill_policy y vuelve a guardar skills del agente.';
      } else if (skillsLoaded.includes('gitlab-integration') && toolsExecuted.length === 0) {
        const looksLikeToolJson = /"tool_calls"\s*:/.test(data.response || '');
        warning = looksLikeToolJson
          ? 'El modelo emitio tool_calls pero no se ejecutaron en el servidor. Reintenta; si persiste, revisa la credencial de GitLab en Organizacion → Conexiones.'
          : 'GitLab cargada pero no se ejecutaron tools en este turno. Pide explicitamente, ej.: "crea el issue en gitlab para este ticket".';
      }
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      setAgentInfo({
        agent: data.agent_used,
        skillsConfigured,
        skillsLoaded,
        toolsExecuted,
        warning,
        turnHealth,
        transportCode: null,
      });

      if (data.artifacts_created?.length > 0) {
        toast.success(`Artefacto(s) creados: ${data.artifacts_created.map(a => a.filename).join(', ')}`);
        if (onArtifactCreated) onArtifactCreated();
      }
    } catch (err) {
      const status = err?.response?.status;
      const isTimeout =
        err?.code === 'ECONNABORTED' ||
        status === 504 ||
        /timeout/i.test(err?.message || '');
      const transportCode = isTimeout ? 'http_gateway_timeout' : 'http_error';
      const msg = isTimeout
        ? (
          'El gateway o el cliente corto el turno (presupuesto HTTP / nginx). '
          + 'No es un fallo del modelo: revisá timeouts del proxy host y presupuestos Runtime/capsula. '
          + `Autopsia: ${autopsyConvId}. Recarga workspace/historial por si ya se escribieron artefactos.`
        )
        : `Error al procesar tu mensaje: ${formatApiError(err)}`;
      toast.error(isTimeout ? 'Timeout de gateway (504 / cliente)' : 'Error en el chat del ticket');
      setMessages(prev => [...prev, { role: 'assistant', content: msg }]);
      setAgentInfo((prev) => ({
        ...prev,
        transportCode,
        warning: isTimeout
          ? 'Turno cortado por transporte. Recargá el workspace y abrí la autopsia si estaba Activa.'
          : prev.warning,
      }));
      if (isTimeout && onArtifactCreated) onArtifactCreated();
      loadHistory();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Agent info */}
      <div className="px-3 py-2 border-b border-brand-border bg-zinc-50 space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <Sparkles className="w-3.5 h-3.5 text-brand-primary" />
            <span className="text-[11px] font-medium text-zinc-600">
              Asistente del Ticket
            </span>
            {agentInfo.agent && (
              <Badge className="text-[9px] rounded-sm bg-brand-primary/10 text-brand-primary">{agentInfo.agent}</Badge>
            )}
          </div>
          {agentInfo.skillsLoaded.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap justify-end">
              {agentInfo.skillsLoaded.map(s => (
                <Badge key={s} variant="secondary" className="text-[9px] rounded-sm bg-blue-50 text-blue-700" data-testid={`ticket-chat-skill-${s}`}>{s}</Badge>
              ))}
            </div>
          )}
        </div>
        {agentInfo.toolsExecuted.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap" data-testid="ticket-chat-tools-executed">
            <span className="text-[9px] text-zinc-500">Tools:</span>
            {agentInfo.toolsExecuted.map(t => (
              <Badge key={t} variant="outline" className="text-[9px] rounded-sm">{t}</Badge>
            ))}
          </div>
        )}
        {agentInfo.warning && (
          <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1" data-testid="ticket-chat-skill-warning">
            {agentInfo.warning}
          </p>
        )}
        {agentInfo.transportCode === 'http_gateway_timeout' && (
          <p className="text-[10px] text-red-800 bg-red-50 border border-red-200 rounded-sm px-2 py-1" data-testid="ticket-chat-gateway-timeout">
            Codigo cliente: <code className="bg-white/80 px-0.5">http_gateway_timeout</code>
            {' — '}el runtime no pudo devolver Turn Health porque el proxy corto la request.
          </p>
        )}
        {agentInfo.turnHealth && agentInfo.turnHealth.status && agentInfo.turnHealth.status !== 'ok' && (
          <div
            className="text-[10px] text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5 space-y-1"
            data-testid="ticket-chat-turn-health"
          >
            <p className="font-semibold">
              Turno degradado ({(agentInfo.turnHealth.codes || []).join(', ')})
            </p>
            {(agentInfo.turnHealth.hints || []).slice(0, 3).map((h) => (
              <p key={h.code}>{h.message}</p>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/logs?tab=autopsy&conversation=${encodeURIComponent(autopsyConvId)}`}
            className="text-[10px] text-brand-primary hover:underline inline-flex items-center gap-0.5"
            data-testid="ticket-chat-autopsy-link"
          >
            <ExternalLink className="w-3 h-3" /> Ver autopsia ({autopsyConvId})
          </Link>
          <button
            type="button"
            className="text-[10px] text-zinc-500 hover:text-zinc-800 underline"
            onClick={() => { loadHistory(); if (onArtifactCreated) onArtifactCreated(); }}
            data-testid="ticket-chat-reload"
          >
            Recargar historial / workspace
          </button>
        </div>
        {!agentInfo.agent && (
          <p className="text-[9px] text-zinc-400">
            Credenciales de skill: &quot;Guardar Skill&quot;. Toggle del agente: &quot;Guardar skills&quot; (boton inferior).
            Investigaciones largas deben caber bajo el presupuesto HTTP de plataforma (ver Capsula → Coordinacion).
          </p>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-auto p-3 space-y-2" data-testid="ticket-chat-messages">
        {loadingHistory && (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
          </div>
        )}
        {!loadingHistory && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-zinc-400 text-center px-4">
            <Bot className="w-8 h-8 mb-2 opacity-30" />
            <p className="text-xs font-medium">Asistente Inteligente</p>
            <p className="text-[11px] mt-1 leading-relaxed">
              Consulta sobre el ticket, pide analisis, o genera documentos.<br />
              Contesta el agente que tiene el caso, con sus habilidades.
            </p>
          </div>
        )}
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-3 py-2 rounded-sm text-sm ${
              msg.role === 'user'
                ? 'bg-brand-primary text-white'
                : 'bg-white text-[#09090B] border border-brand-border'
            }`}>
              <div className="flex items-center gap-1 mb-0.5">
                {msg.role === 'user' ? <User className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
                <span className="text-[10px] opacity-60">{msg.role === 'user' ? 'Tu' : 'Asistente'}</span>
              </div>
              <p className="whitespace-pre-wrap leading-relaxed text-xs">{msg.content}</p>
              {msg.timestamp && (
                <div className="flex justify-end mt-0.5">
                  <span className={`text-[10px] ${msg.role === 'user' ? 'text-white/50' : 'text-zinc-400'}`}>
                    {formatTime(msg.timestamp)}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-brand-border rounded-sm px-3 py-2 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin text-brand-primary" />
              <span className="text-xs text-zinc-400">Pensando...</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="p-2 border-t border-brand-border">
        <div className="flex gap-2">
          <Input
            data-testid="ticket-chat-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder="Consulta, analiza, o pide generar un documento..."
            className="flex-1 rounded-sm text-xs h-8"
            disabled={loading}
          />
          <Button
            data-testid="ticket-chat-send"
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            size="sm"
            className="bg-brand-primary text-white rounded-sm h-8 px-3"
          >
            <Send className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Conversation Thread Viewer (multi-hilo) ──
function ConversationThread({ ticket, tenantId, capsuleId, onClose }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [found, setFound] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [channel, setChannel] = useState(null);
  const [threads, setThreads] = useState([]);

  useEffect(() => {
    loadThreads();
  }, [ticket.ticket_id]);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const listRes = await ticketsAPI.getConversations(tenantId, capsuleId, ticket.ticket_id);
      const list = listRes.data?.conversations || [];
      setThreads(list);
      const primary = listRes.data?.primary_conversation_id || list[0]?.conversation_id || null;
      if (primary) {
        await loadConversation(primary);
      } else {
        const res = await ticketsAPI.getConversation(tenantId, capsuleId, ticket.ticket_id);
        setMessages(res.data.messages || []);
        setFound(res.data.found);
        setConversationId(res.data.conversation_id || null);
        setChannel(res.data.channel || null);
      }
    } catch {
      setMessages([]);
      setThreads([]);
      setConversationId(null);
    } finally {
      setLoading(false);
    }
  };

  const loadConversation = async (cid) => {
    try {
      const res = await ticketsAPI.getConversation(tenantId, capsuleId, ticket.ticket_id, {
        conversation_id: cid,
      });
      setMessages(res.data.messages || []);
      setFound(res.data.found);
      setConversationId(res.data.conversation_id || cid);
      setChannel(res.data.channel || null);
    } catch {
      setMessages([]);
      setFound(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-2 border-b border-brand-border flex items-center justify-between bg-zinc-50">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-700">Conversaciones</span>
          <Badge variant="outline" className="text-[9px] rounded-sm">{threads.length || (found ? 1 : 0)} hilos</Badge>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
      {threads.length > 1 && (
        <div className="px-3 py-2 border-b border-brand-border bg-white space-y-1 max-h-28 overflow-auto" data-testid="ticket-conversation-list">
          {threads.map((t) => (
            <button
              key={t.conversation_id}
              type="button"
              onClick={() => loadConversation(t.conversation_id)}
              className={`w-full text-left px-2 py-1.5 rounded-sm text-[11px] border ${
                conversationId === t.conversation_id
                  ? 'border-brand-primary bg-brand-surface'
                  : 'border-transparent hover:bg-zinc-50'
              }`}
            >
              <div className="font-medium truncate">{t.title || t.conversation_id}</div>
              <div className="text-[10px] text-zinc-500 flex gap-2">
                <span>{t.channel || '—'}</span>
                <span>{t.status || '—'}</span>
                <span>{t.message_count ?? 0} msgs</span>
              </div>
            </button>
          ))}
        </div>
      )}
      <div className="flex-1 overflow-auto p-4 space-y-2" data-testid="ticket-conversation-thread">
        {!found && (
          <div className="text-center text-zinc-400 text-xs py-8">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
            No se encontro conversacion vinculada a este ticket.
          </div>
        )}
        {messages.map((msg, idx) => {
          const isUser = msg.sender_type === 'user';
          const isHuman = msg.sender_type === 'human';
          const isSystem = msg.sender_type === 'system';
          return (
            <div key={msg.message_id || idx} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-sm text-xs ${
                isUser ? 'bg-zinc-700 text-white'
                : isHuman ? 'bg-emerald-50 border border-emerald-200 text-[#09090B]'
                : isSystem ? 'bg-brand-warning/10 border border-brand-warning/30 text-zinc-700'
                : 'bg-white border border-brand-border text-[#09090B]'
              }`}>
                <div className="flex items-center gap-1 mb-0.5 opacity-70">
                  {isUser && <User className="w-2.5 h-2.5" />}
                  {msg.sender_type === 'agent' && <Bot className="w-2.5 h-2.5" />}
                  {isHuman && <Shield className="w-2.5 h-2.5" />}
                  {isSystem && <AlertTriangle className="w-2.5 h-2.5" />}
                  <span className="text-[10px] font-mono">{msg.sender_name || msg.sender_type}</span>
                  {msg.timestamp && (
                    <span className="text-[10px] ml-auto opacity-50" title={formatDateTime(msg.timestamp)}>
                      {formatTime(msg.timestamp)}
                    </span>
                  )}
                </div>
                <EmailMessageContext
                  message={msg}
                  channel={channel}
                  isUserBubble={isUser || isHuman}
                />
                <EmailMessageBody
                  message={msg}
                  channel={channel}
                  isUserBubble={isUser || isHuman}
                />
                <MessageAttachments
                  conversationId={conversationId}
                  message={msg}
                  isUserBubble={isUser}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── Ticket Reply Panel (respond to user via original channel) ──
const CHANNEL_LABELS = {
  webchat: { label: 'WebChat', icon: Monitor, color: 'text-blue-600 bg-blue-50' },
  whatsapp: { label: 'WhatsApp', icon: Phone, color: 'text-emerald-600 bg-emerald-50' },
  teams: { label: 'Teams', icon: Monitor, color: 'text-purple-600 bg-purple-50' },
  email: { label: 'Email', icon: Mail, color: 'text-orange-600 bg-orange-50' },
};

function TicketReplyPanel({ ticket, tenantId, capsuleId }) {
  const [messages, setMessages] = useState([]);
  const [channelInfo, setChannelInfo] = useState(null);
  const [linkedConversationId, setLinkedConversationId] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // Lo que esta por salir de la empresa, mientras alguien lo mira. `null` = no hay nada en vuelo.
  const [previewDeRespuesta, setPreviewDeRespuesta] = useState(null);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => { loadData(); }, [ticket.ticket_id]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [convRes, chRes] = await Promise.all([
        ticketsAPI.getConversation(tenantId, capsuleId, ticket.ticket_id),
        ticketsAPI.getChannelInfo(tenantId, capsuleId, ticket.ticket_id),
      ]);
      setMessages(convRes.data.messages || []);
      setLinkedConversationId(convRes.data.conversation_id || null);
      setChannelInfo(chRes.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  /**
   * **Esto sale de la empresa.** El mensaje va por WhatsApp, mail o Teams **al solicitante**, y se
   * mandaba desde un `Input` de una linea, con Enter, sin preview y sin confirmacion: un Enter de
   * mas y el cliente recibe algo a medio escribir. No es un borrador que alguien revisa despues —
   * una vez que salio, salio.
   *
   * No se bloquea el Enter sin mas: se le da al Enter el trabajo de **abrir el preview**, que es lo
   * que el gesto ya hacia esperar (mandar) pero con una pantalla de por medio que muestra por donde
   * sale y a quien.
   */
  const handleSend = () => {
    if (!input.trim() || sending) return;
    setPreviewDeRespuesta(input.trim());
  };

  const confirmarEnvio = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const res = await ticketsAPI.replyToUser(tenantId, capsuleId, ticket.ticket_id, {
        message: input.trim(),
      });
      const data = res.data;
      const delivery = data.delivery || {};
      if (delivery.delivered) {
        toast.success(`Mensaje enviado por ${data.channel}`);
      } else if (delivery.queued) {
        toast.info(`Mensaje encolado. ${delivery.reason}`);
      } else {
        toast.success('Mensaje guardado en conversacion');
      }
      setInput('');
      setPreviewDeRespuesta(null);
      // Reload conversation
      await loadData();
    } catch (err) {
      toast.error('Error al enviar respuesta');
    } finally {
      setSending(false);
    }
  };

  const ch = CHANNEL_LABELS[channelInfo?.channel] || CHANNEL_LABELS.webchat;
  const ChannelIcon = ch.icon;

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Channel info header */}
      <div className="px-4 py-2 border-b border-brand-border bg-zinc-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Reply className="w-3.5 h-3.5 text-zinc-500" />
            <span className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-700">Responder al Usuario</span>
          </div>
          {channelInfo?.has_conversation && (
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-sm text-xs font-medium ${ch.color}`}>
              <ChannelIcon className="w-3.5 h-3.5" />
              <span>Canal: {ch.label}</span>
              {channelInfo.channel_user_id && (
                <span className="font-mono text-[10px] opacity-70">({channelInfo.channel_user_id.slice(0, 12)})</span>
              )}
            </div>
          )}
        </div>
        {channelInfo?.has_conversation && channelInfo.channel !== 'webchat' && (
          <p className="text-[10px] text-zinc-400 mt-1">
            Las respuestas se enviaran por <strong>{ch.label}</strong> al usuario. Si la integracion no esta activa, el mensaje quedara encolado.
          </p>
        )}
        {!channelInfo?.has_conversation && (
          <p className="text-[10px] text-brand-danger mt-1">No hay conversacion vinculada a este ticket.</p>
        )}
      </div>

      {/* Messages (read-only conversation + ability to reply) */}
      <div className="flex-1 overflow-auto p-4 space-y-1" data-testid="ticket-reply-messages">
        {messages.length === 0 && (
          <div className="text-center text-zinc-400 text-xs py-8">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
            Sin mensajes en la conversacion.
          </div>
        )}
        {messages.map((msg, idx) => {
          const isUser = msg.sender_type === 'user';
          const isHuman = msg.sender_type === 'human';
          const isSystem = msg.sender_type === 'system';
          const isAgent = msg.sender_type === 'agent';
          const isTicketReply = msg.metadata?.sent_via === 'ticket_reply';
          return (
            <div key={msg.message_id || idx} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-0.5`}>
              <div className={`max-w-[75%] px-3 py-2 rounded-sm text-xs ${
                isUser ? 'bg-zinc-700 text-white'
                : isHuman && isTicketReply ? 'bg-brand-primary text-white'
                : isHuman ? 'bg-emerald-50 border border-emerald-200 text-[#09090B]'
                : isSystem ? 'bg-brand-warning/10 border border-brand-warning/30 text-zinc-700'
                : 'bg-white border border-brand-border text-[#09090B]'
              }`}>
                <div className="flex items-center gap-1 mb-0.5 opacity-70">
                  {isUser && <User className="w-2.5 h-2.5" />}
                  {isAgent && <Bot className="w-2.5 h-2.5" />}
                  {isHuman && <Shield className="w-2.5 h-2.5" />}
                  {isSystem && <AlertTriangle className="w-2.5 h-2.5" />}
                  <span className="text-[10px] font-mono">{msg.sender_name || msg.sender_type}</span>
                  {isTicketReply && <Badge className="text-[8px] bg-white/20 rounded-sm px-1 py-0 ml-1">via ticket</Badge>}
                  {msg.timestamp && (
                    <span
                      className={`text-[10px] ml-auto ${isUser || (isHuman && isTicketReply) ? 'opacity-50' : 'text-zinc-400'}`}
                      title={formatDateTime(msg.timestamp)}
                    >
                      {formatTime(msg.timestamp)}
                    </span>
                  )}
                </div>
                <EmailMessageContext
                  message={msg}
                  channel={channelInfo?.channel}
                  isUserBubble={isUser || (isHuman && isTicketReply)}
                />
                <EmailMessageBody
                  message={msg}
                  channel={channelInfo?.channel}
                  isUserBubble={isUser || (isHuman && isTicketReply)}
                />
                  <MessageAttachments
                    conversationId={linkedConversationId}
                    message={msg}
                    isUserBubble={isUser}
                  />
                ) : null}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Reply input */}
      {channelInfo?.has_conversation && (
        <div className="p-3 border-t border-brand-border bg-white">
          <div className="flex items-center gap-1.5 mb-1.5">
            <ChannelIcon className="w-3 h-3 text-zinc-400" />
            <span className="text-[10px] text-zinc-400">Respondiendo por {ch.label}</span>
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="ticket-reply-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder="Escribe tu respuesta al usuario..."
              className="flex-1 rounded-sm text-xs h-9"
              disabled={sending}
            />
            <Button
              data-testid="ticket-reply-send"
              onClick={handleSend}
              disabled={sending || !input.trim()}
              size="sm"
              className="bg-brand-primary text-white rounded-sm h-9 px-4"
            >
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            </Button>
          </div>
          <p className="text-[10px] text-zinc-400 mt-1">
            Sale al solicitante por {ch.label}. Enter muestra como queda antes de mandarlo.
          </p>

          {/* Mandar un mensaje al cliente no destruye nada —no va en rojo— pero **no se puede
              deshacer**, que es lo que el preview viene a cubrir: se ve el texto completo, por
              donde sale y a quien, antes de que salga. */}
          <ConfirmActionDialog
            open={previewDeRespuesta !== null}
            onOpenChange={(abierto) => { if (!abierto) setPreviewDeRespuesta(null); }}
            title={`Mandar la respuesta por ${ch.label}`}
            description="Sale al solicitante del caso. Una vez enviado no se puede deshacer."
            confirmText="Mandar"
            destructivo={false}
            onConfirm={confirmarEnvio}
          >
            <div className="rounded-sm border border-brand-border bg-zinc-50 p-3">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                Como lo va a recibir
              </p>
              <p className="text-sm whitespace-pre-wrap break-words" data-testid="preview-de-respuesta">
                {previewDeRespuesta}
              </p>
            </div>
          </ConfirmActionDialog>
        </div>
      )}
    </div>
  );
}

function artifactRelPath(art) {
  const sub = (art.subfolder || '').trim();
  return sub ? `${sub}/${art.filename}` : art.filename;
}

function WorkspacePanel({ ticket, tenantId, capsuleId, refreshToken, onRequestConfirm }) {
  const [workspace, setWorkspace] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [tree, setTree] = useState({ tree: [], folders: {} });
  const [selectedId, setSelectedId] = useState(null);
  const [visorAbierto, setVisorAbierto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newPath, setNewPath] = useState('');
  const [newContent, setNewContent] = useState('# Nuevo documento\n');
  const [creating, setCreating] = useState(false);

  const selected = artifacts.find((a) => a.artifact_id === selectedId) || null;

  const loadWorkspaceData = async () => {
    if (!ticket?.ticket_id || !tenantId || !capsuleId) return;
    try {
      const [wsRes, artRes, treeRes] = await Promise.all([
        ticketsAPI.getWorkspace(tenantId, capsuleId, ticket.ticket_id).catch(() => ({ data: null })),
        ticketsAPI.getArtifacts(tenantId, capsuleId, ticket.ticket_id),
        ticketsAPI.getWorkspaceTree(tenantId, capsuleId, ticket.ticket_id),
      ]);
      setWorkspace(wsRes.data);
      const arts = artRes.data || [];
      setArtifacts(arts);
      setTree(treeRes.data || { tree: [], folders: {} });
      if (selectedId && !arts.some((a) => a.artifact_id === selectedId)) {
        // `selected` es derivado de `selectedId`, asi que limpiarlo alcanza. Aca habia ademas
        // un `setEditContent('')` que **no existe en este componente**: quedo de un refactor y
        // rompia la pantalla justo en el camino de limpieza, cuando el artefacto abierto
        // desaparecia. Lo encontro `no-undef` el 2026-09-01, no una persona usando el producto.
        setSelectedId(null);
      }
    } catch {
      setWorkspace(null);
      setArtifacts([]);
      setTree({ tree: [], folders: {} });
    }
  };

  useEffect(() => {
    loadWorkspaceData();
  }, [ticket?.ticket_id, tenantId, capsuleId, refreshToken]);


  const folderKeys = Object.keys(tree.folders || {}).sort((a, b) => {
    if (a === '/') return -1;
    if (b === '/') return 1;
    return a.localeCompare(b);
  });

  const handleSave = async (contenido) => {
    if (!selected || !ticket) return;
    setSaving(true);
    try {
      await ticketsAPI.updateArtifact(tenantId, capsuleId, ticket.ticket_id, selected.artifact_id, {
        content: contenido,
      });
      toast.success('Artefacto guardado');
      await loadWorkspaceData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!selected || !ticket) return;
    onRequestConfirm({
      title: 'Eliminar artefacto',
      description: `Se eliminará "${artifactRelPath(selected)}" del workspace. Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      onConfirm: async () => {
        try {
          await ticketsAPI.deleteArtifact(tenantId, capsuleId, ticket.ticket_id, selected.artifact_id);
          toast.success('Artefacto eliminado');
          // Mismo resto de refactor: **borrar un artefacto rompia la pantalla despues de
          // borrarlo**, con el toast de exito ya mostrado.
          setSelectedId(null);
          await loadWorkspaceData();
        } catch (err) {
          toast.error(formatApiError(err));
        }
      },
    });
  };

  const handleCreate = async () => {
    const path = newPath.trim();
    if (!path) {
      toast.error('Indica la ruta del archivo (ej. analisis/informe.md)');
      return;
    }
    setCreating(true);
    try {
      const filename = path.includes('/') ? path.split('/').pop() : path;
      await ticketsAPI.createArtifact(tenantId, capsuleId, ticket.ticket_id, {
        path,
        filename,
        content: newContent,
        type: 'markdown',
      });
      toast.success('Artefacto creado');
      setCreateOpen(false);
      setNewPath('');
      setNewContent('# Nuevo documento\n');
      await loadWorkspaceData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const handleReindex = async () => {
    setReindexing(true);
    try {
      const res = await ticketsAPI.reindexWorkspace(tenantId, capsuleId, ticket.ticket_id);
      const data = res.data;
      toast.success(`Workspace indexado en KB: ${data.indexed}/${data.total_artifacts} artefactos`);
    } catch {
      toast.error('Error al indexar workspace');
    } finally {
      setReindexing(false);
    }
  };

  return (
    <Card className="border border-brand-border rounded-sm">
      <CardHeader className="pb-2 border-b border-brand-border">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-2">
            <FolderTree className="w-3.5 h-3.5" />
            Workspace
            <Badge variant="secondary" className="text-[9px] rounded-sm">{artifacts.length} archivos</Badge>
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] text-emerald-600 flex items-center gap-1">
              <Database className="w-3 h-3" /> Indexación automática en KB
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCreateOpen(true)}
              className="text-[10px] h-6 rounded-sm gap-1"
              data-testid="workspace-new-file-btn"
            >
              <Plus className="w-3 h-3" /> Nuevo
            </Button>
            <Button
              data-testid="reindex-workspace-btn"
              variant="outline"
              size="sm"
              onClick={handleReindex}
              disabled={reindexing || artifacts.length === 0}
              className="text-[10px] h-6 rounded-sm gap-1"
            >
              {reindexing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Re-indexar
            </Button>
          </div>
        </div>
        <p className="text-[10px] text-zinc-400 mt-1 font-mono">
          Tickets / {ticket.ticket_id.slice(0, 8)} / {workspace?.workspace_name || '...'}
        </p>
      </CardHeader>
      <CardContent className="p-4">
        {artifacts.length === 0 ? (
          <p className="text-xs text-zinc-400">Sin artefactos. Crea uno manualmente o usa el Asistente IA.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4">
            <div className="border border-brand-border rounded-sm p-2 max-h-72 overflow-y-auto" data-testid="workspace-tree">
              {folderKeys.map((folderKey) => {
                const entries = (tree.folders || {})[folderKey] || [];
                const label = folderKey === '/' ? 'Raíz' : folderKey;
                return (
                  <div key={folderKey} className="mb-2">
                    <p className="text-[10px] font-semibold uppercase text-zinc-500 px-1 mb-1">{label}</p>
                    <div className="space-y-0.5">
                      {entries.map((entry) => (
                        <button
                          key={entry.artifact_id}
                          type="button"
                          data-testid={`artifact-tree-${entry.artifact_id}`}
                          onClick={() => { setSelectedId(entry.artifact_id); setVisorAbierto(true); }}
                          className={`w-full text-left flex items-center gap-1.5 px-2 py-1 rounded-sm text-xs font-mono transition-colors ${
                            selectedId === entry.artifact_id
                              ? 'bg-brand-primary/10 text-brand-primary'
                              : 'hover:bg-zinc-50 text-zinc-700'
                          }`}
                        >
                          <FileText className="w-3 h-3 shrink-0" />
                          <span className="truncate">{entry.filename}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* El contenido se lee en una modal, no aca. Un informe de dos paginas en una
                columna de un panel —y en un textarea monoespaciado sobre fondo negro— no se
                lee: se descifra. El arbol queda para navegar. */}
            <div className="min-h-[180px] flex items-center justify-center border border-dashed border-brand-border rounded-sm">
              <p className="text-xs text-zinc-400 px-4 text-center">
                Hace clic en un archivo para abrirlo.
              </p>
            </div>
          </div>
        )}
      </CardContent>

      <ArtifactDialog
        open={visorAbierto && !!selected}
        onOpenChange={setVisorAbierto}
        artifact={selected}
        rutaRelativa={selected ? artifactRelPath(selected) : ''}
        onSave={handleSave}
        onDelete={handleDelete}
        saving={saving}
      />

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuevo artefacto</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-xs">Ruta relativa</Label>
              <Input
                value={newPath}
                onChange={(e) => setNewPath(e.target.value)}
                placeholder="analisis/informe.md"
                className="mt-1 text-xs h-8 font-mono"
                data-testid="workspace-new-path"
              />
            </div>
            <div>
              <Label className="text-xs">Contenido Markdown</Label>
              <Textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                className="mt-1 min-h-[160px] font-mono text-xs"
                data-testid="workspace-new-content"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setCreateOpen(false)} className="text-xs h-8">
                Cancelar
              </Button>
              <Button size="sm" onClick={handleCreate} disabled={creating} className="text-xs h-8" data-testid="workspace-create-btn">
                {creating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Crear
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


export default function TicketsPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [workspaceRefreshToken, setWorkspaceRefreshToken] = useState(0);
  const [filter, setFilter] = useState('');
  // **La busqueda va al servidor, y se aplica al Enter.** Filtraba en el cliente sobre los 50
  // casos traidos, asi que el caso de hace dos meses no aparecia nunca y nada lo decia. Al Enter y
  // no al tipear, igual que en Mis Solicitudes: una consulta por tecla sobre varias capsulas es
  // una consulta por tecla sobre varias capsulas.
  const [busquedaAplicada, setBusquedaAplicada] = useState('');
  // Quien lo tiene. `sin_asignar` es la cola: los casos que esperan a que alguien los tome, que
  // hasta hoy no se podian ver desde esta pantalla de ninguna manera.
  const [filtroDuenio, setFiltroDuenio] = useState('');
  // **Los seis filtros que el backend ya tenia y la UI no mandaba.** `ticket_list_filters`
  // implementa estado, alcance, prioridad, gerencia y texto en notas desde hace meses, y la
  // bandeja no enviaba ninguno: la persona filtraba a ojo sobre lo que hubiera venido.
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroPrioridad, setFiltroPrioridad] = useState('');
  const [filtroGerencia, setFiltroGerencia] = useState('');
  const [filtroVence, setFiltroVence] = useState('');
  // **El orden que pidió la gerencia, como opción y no como default (2026-10-09).** El jefe de
  // quien pidió ordena sus casos en *Mis solicitudes* y, hasta acá, ninguna pantalla de trabajo
  // lo leía. El default sigue siendo el compromiso: contesta «qué hago primero» para todos.
  const [orden, setOrden] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: '', description: '', priority: 'medium',
    reporter: { name: '', email: '', contact_id: null },
  });
  const [creating, setCreating] = useState(false);
  const [accessibleCapsules, setAccessibleCapsules] = useState([]);
  const [createCapsuleId, setCreateCapsuleId] = useState('');
  const [createIntent, setCreateIntent] = useState('');
  const [createTemplateId, setCreateTemplateId] = useState('');
  const [createApplyWorkflow, setCreateApplyWorkflow] = useState(true);
  const [createTemplates, setCreateTemplates] = useState([]);
  const [createIntents, setCreateIntents] = useState([]);
  const [createCategories, setCreateCategories] = useState([]);
  const [createPriorities, setCreatePriorities] = useState([]);
  const [createDepartments, setCreateDepartments] = useState([]);
  const [createCategory, setCreateCategory] = useState('');
  const [createDepartment, setCreateDepartment] = useState('');
  const [activeTab, setActiveTab] = useState('detail');
  const [confirmState, setConfirmState] = useState({ open: false, title: '', description: '', onConfirm: null, confirmText: 'Confirmar' });
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgePhrase, setPurgePhrase] = useState('');
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [scrubOpen, setScrubOpen] = useState(false);
  const [scrubPhrase, setScrubPhrase] = useState('');
  const [scrubLoading, setScrubLoading] = useState(false);
  const PURGE_TICKETS_PHRASE = 'BORRAR TICKETS';
  const SCRUB_PHRASE = 'LIMPIAR HUERFANOS';
  const isAdmin = user?.role === 'admin';
  const [tmIntent, setTmIntent] = useState('');
  const [tmCategory, setTmCategory] = useState('');
  const [tmLabels, setTmLabels] = useState([]);
  const [tmPriority, setTmPriority] = useState('medium');
  const [tmDepartment, setTmDepartment] = useState('');
  const [tmSaving, setTmSaving] = useState(false);
  const [capsuleLabels, setCapsuleLabels] = useState([]);
  const [capsuleIntents, setCapsuleIntents] = useState([]);
  const [capsuleCategories, setCapsuleCategories] = useState([]);
  const [capsulePriorities, setCapsulePriorities] = useState([]);
  const [capsuleTicketStatuses, setCapsuleTicketStatuses] = useState([]);
  const [capsuleDepartments, setCapsuleDepartments] = useState([]);
  // Quien puede clasificar lo decide el backend con `responsabilidad.puede('clasificar')`:
  // admin y supervisor por su rol de consola, y el resto **solo si es el propietario del
  // caso**. Aca decia `['admin','supervisor','operator','executive'].includes(role)`, que no
  // es la misma regla: al operador se le habilitaban los selectores y se comia un 403 al
  // guardar. Ofrecer lo que el backend rechaza es peor que no ofrecerlo — la persona no
  // aprende cual es la regla, aprende que la aplicacion falla.
  const esPropietarioDelCaso = (t) => {
    const duenio = String(t?.propietario?.id || '').trim().toLowerCase();
    const yo = String(user?.email || '').trim().toLowerCase();
    return Boolean(duenio && yo && duenio === yo);
  };
  const mandaSobreElArea = ['admin', 'supervisor'].includes(user?.role);
  const canEditClassification = mandaSobreElArea || esPropietarioDelCaso(selectedTicket);
  const priorityName = (id) => {
    const found = capsulePriorities.find((p) => p.id === id);
    return found?.name || id || '—';
  };
  const priorityStyle = (id) => {
    const found = capsulePriorities.find((p) => p.id === id);
    if (found?.color) return { backgroundColor: found.color, color: '#fff' };
    return undefined;
  };
  const statusName = (id) => {
    const found = capsuleTicketStatuses.find((s) => s.id === id);
    return found?.name || id || '—';
  };
  // **El detalle traducia y la lista no — y la lista es donde se decide.** El badge del header usa
  // `capsuleIntents`; la fila de la cola mostraba `soporte_aplicaciones` crudo. Mismo catalogo, un
  // solo lugar que lo resuelve.
  const intentName = (id) => {
    const found = (capsuleIntents || []).find((i) => i.id === id);
    return found?.name || id || '';
  };
  /**
   * Quien lo tiene, o de que cola es si no lo tiene nadie.
   *
   * **El dato viajaba y el render lo tiraba.** La proyeccion del backend es `{_id: 0}` —el
   * documento entero— asi que `propietario` y `rol_propietario` estaban en el payload de cada fila
   * y la cola no los dibujaba. Consecuencia medida: no habia **ninguna** forma de ver los casos sin
   * dueño desde esta pantalla, y un caso sin dueño es el que se duerme.
   *
   * El texto sale del molde que `MisSolicitudesPage` ya resolvio bien: un caso sin dueño **dice**
   * en que cola esta, en vez de quedar en blanco. Un blanco se lee como "no cargado".
   */
  const duenioDelCaso = (t) => {
    const quien = (t?.propietario?.name || t?.propietario?.id || '').trim();
    if (quien) return { texto: quien, sinDuenio: false };
    const rol = (t?.rol_propietario || '').trim();
    return {
      texto: rol ? `sin dueño — cola de ${rol}` : 'sin dueño y sin rol',
      sinDuenio: true,
    };
  };
  const [detailEditing, setDetailEditing] = useState(false);
  const [detailDraft, setDetailDraft] = useState({ title: '', description: '', objective: '', reporter: { name: '', email: '', contact_id: null } });
  const [cambiandoSolicitante, setCambiandoSolicitante] = useState(false);
  const [detailSaving, setDetailSaving] = useState(false);
  const [narrativeRefreshing, setNarrativeRefreshing] = useState(false);
  const tenantId = user?.tenant_id;
  const [queryCapsuleIds, setQueryCapsuleIds] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightTaskId, setHighlightTaskId] = useState(null);
  // Propiedad del caso: a quien se lo paso, con que rol y por que.
  const [teamRoles, setTeamRoles] = useState([]);
  const [teamMembersTicket, setTeamMembersTicket] = useState([]);
  const [traspasoEmail, setTraspasoEmail] = useState('');
  const [traspasoRol, setTraspasoRol] = useState('');
  const [traspasoMotivo, setTraspasoMotivo] = useState('');
  const [ownerBusy, setOwnerBusy] = useState(false);
  const deepLinkHandled = useRef(false);
  const [ticketsLoaded, setTicketsLoaded] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await capsulesAPI.list(tenantId);
        const allIds = (res.data || [])
          .filter((c) => c?.status !== 'archived')
          .map((c) => c.capsule_id)
          .filter(Boolean);
        const member = (user?.capsule_ids || []).filter(Boolean);
        let ids;
        if (member.length) ids = allIds.filter((id) => member.includes(id));
        else if (user?.role === 'admin' || user?.role === 'supervisor') ids = allIds;
        else ids = [];
        if (!cancelled) setQueryCapsuleIds(ids);
      } catch {
        if (!cancelled) setQueryCapsuleIds((user?.capsule_ids || []).filter(Boolean));
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, user?.capsule_ids, user?.role]);

  useEffect(() => {
    if (!tenantId) return;
    capsulesAPI.list(tenantId).then((res) => {
      setAccessibleCapsules(res.data || []);
    }).catch(() => setAccessibleCapsules([]));
  }, [tenantId]);

  useEffect(() => {
    if (queryCapsuleIds.length && !createCapsuleId) {
      setCreateCapsuleId(queryCapsuleIds[0]);
    }
  }, [queryCapsuleIds, createCapsuleId]);

  useEffect(() => {
    if (!createOpen || !tenantId || !createCapsuleId) return;
    let cancelled = false;
    capsuleConfigAPI.get(tenantId, createCapsuleId).then((res) => {
      if (cancelled) return;
      const cfg = res.data || {};
      setCreateTemplates(cfg.workflow_templates || []);
      setCreateIntents(cfg.intents || []);
      setCreateCategories(cfg.categories || []);
      setCreatePriorities(cfg.priorities || []);
      setCreateDepartments(cfg.requesting_departments || []);
    }).catch(() => {
      if (!cancelled) {
        setCreateTemplates([]);
        setCreateIntents([]);
        setCreateCategories([]);
        setCreatePriorities([]);
        setCreateDepartments([]);
      }
    });
    return () => { cancelled = true; };
  }, [createOpen, tenantId, createCapsuleId]);

  useEffect(() => {
    if (!createIntent) return;
    const suggested = findTemplateForIntent(createTemplates, createIntent);
    if (suggested?.template_id) {
      setCreateTemplateId(suggested.template_id);
    }
  }, [createIntent, createTemplates]);

  useEffect(() => {
    if (tenantId) loadTickets();
  }, [tenantId, queryCapsuleIds, filtroEstado, filtroPrioridad, filtroGerencia, filtroVence,
      busquedaAplicada, filtroDuenio, orden]);

  /**
   * Los catalogos de los filtros, **al entrar y de todas las capsulas de la vista**.
   *
   * Que se rompia, medido: `capsuleTicketStatuses`, `capsulePriorities` y `capsuleDepartments` se
   * cargaban **solo** en el efecto que depende de `selectedTicket`. Al entrar a `/tickets` sin
   * ningun caso abierto, "Estado" ofrecia dos opciones y "Prioridad" y "Gerencia" ninguna: habia
   * que clickear un caso cualquiera para que aparecieran los filtros. Y despues quedaban
   * **contaminados**, con el catalogo de la capsula del caso que abriste, sobre una lista que cruza
   * todas: filtrar por un estado de la capsula A hacia desaparecer en silencio los casos de la B.
   *
   * Se juntan por **union** y no por interseccion, por el mismo criterio que el barrido de estados
   * terminales: ofrecer un filtro de mas se nota —no trae nada y se vuelve atras— y esconder uno
   * no. Se desduplica por `id`, que es lo que viaja en la consulta.
   */
  useEffect(() => {
    if (!tenantId || !queryCapsuleIds.length) return;
    let vigente = true;
    (async () => {
      const resultados = await Promise.allSettled(
        queryCapsuleIds.map((cid) => capsuleConfigAPI.get(tenantId, cid)),
      );
      if (!vigente) return;
      const unir = (campo) => {
        const porId = new Map();
        for (const r of resultados) {
          if (r.status !== 'fulfilled') continue;
          for (const x of (r.value.data?.[campo] || [])) {
            if (x?.id && !porId.has(x.id)) porId.set(x.id, x);
          }
        }
        return [...porId.values()];
      };
      setCapsuleTicketStatuses(unir('ticket_statuses'));
      setCapsulePriorities(unir('priorities'));
      setCapsuleDepartments(unir('requesting_departments'));
      setCapsuleIntents(unir('intents'));
    })();
    return () => { vigente = false; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [tenantId, queryCapsuleIds]);

  useEffect(() => {
    if (!selectedTicket) return;
    setTmIntent(selectedTicket.intent || '');
    setTmCategory(selectedTicket.category || '');
    const lbls = selectedTicket.labels?.length
      ? selectedTicket.labels
      : (selectedTicket.label ? [selectedTicket.label] : []);
    setTmLabels(lbls);
    setTmPriority(selectedTicket.priority || 'medium');
    setTmDepartment(selectedTicket.requesting_department || '');
    setDetailDraft({
      title: selectedTicket.title || '',
      description: selectedTicket.description || '',
      objective: selectedTicket.objective || '',
      reporter: {
        name: selectedTicket.reporter?.name || '',
        email: selectedTicket.reporter?.email || '',
        contact_id: selectedTicket.contact_id || null,
      },
    });
    setDetailEditing(false);
    const cid = selectedTicket.capsule_id || queryCapsuleIds[0];
    if (tenantId && cid) {
      capsuleConfigAPI.get(tenantId, cid)
        .then((r) => {
          const cfg = r.data || {};
          setCapsuleLabels(cfg.labels || []);
          setCapsuleIntents(cfg.intents || []);
          setCapsuleCategories(cfg.categories || []);
          setCapsulePriorities(cfg.priorities || []);
          setCapsuleDepartments(cfg.requesting_departments || []);
          setCapsuleTicketStatuses(cfg.ticket_statuses || []);
          setTeamRoles(cfg.team_roles || []);
        })
        .catch(() => {
          setCapsuleLabels([]);
          setCapsuleIntents([]);
          setCapsuleCategories([]);
          setCapsulePriorities([]);
          setCapsuleDepartments([]);
          setCapsuleTicketStatuses([]);
        });
    }
  }, [selectedTicket?.ticket_id, selectedTicket?.intent, selectedTicket?.category, selectedTicket?.label, selectedTicket?.labels, selectedTicket?.priority, selectedTicket?.requesting_department, selectedTicket?.objective, selectedTicket?.title, selectedTicket?.description, tenantId, queryCapsuleIds]);

  const loadTickets = async () => {
    if (!tenantId || !queryCapsuleIds.length) {
      setTickets([]);
      setTicketsLoaded(true);
      return;
    }
    setTicketsLoaded(false);
    try {
      // Lo que se filtra se filtra **en el servidor**: filtrar despues de traer 50 casos
      // muestra "3 vencidos" cuando hay 40, y esa es la clase de numero sobre el que alguien
      // decide que no hay que hacer nada.
      const params = {};
      if (filtroEstado === 'abiertos') params.status_scope = 'open';
      else if (filtroEstado) params.status = filtroEstado;
      if (filtroPrioridad) params.priority = filtroPrioridad;
      if (filtroGerencia) params.requesting_department = filtroGerencia;
      if (filtroVence) params.vence = filtroVence;
      if (busquedaAplicada) params.q = busquedaAplicada;
      if (filtroDuenio === '__mios__') params.duenio = user?.email || '';
      else if (filtroDuenio) params.duenio = filtroDuenio;
      if (orden) params.orden = orden;
      const results = await Promise.allSettled(
        queryCapsuleIds.map((cid) => ticketsAPI.list(tenantId, cid, params)),
      );
      const merged = [];
      for (const r of results) {
        if (r.status === 'fulfilled') merged.push(...(r.value.data || []));
      }
      const dedup = Object.values(merged.reduce((acc, t) => {
        acc[t.ticket_id] = t;
        return acc;
      }, {}));
      // **La bandeja se ordena por lo que vence, y ese orden lo decide el backend.** Acá sólo
      // se **mezclan** las listas de varias cápsulas, que ya vienen ordenadas cada una: no se
      // re-deriva la regla. Hasta el 2026-08-24 esta línea ordenaba por `created_at`
      // descendente y tiraba a la basura el orden del servidor — la pantalla que tiene que
      // organizar el día mostraba primero lo último que entró.
      //
      // Los casos sin compromiso van al final: `sin_sla` es un estado legítimo, no un
      // vencimiento infinito. El día que el listado sea multi-cápsula del lado del servidor,
      // esta mezcla desaparece.
      //
      // Con el orden pedido, los priorizados van primero por `posicion_pedida`, que manda el
      // servidor: la mezcla usa la posición que ya viene, no la recalcula.
      dedup.sort((a, b) => {
        if (orden === 'prioridad') {
          // Primero la franja: lo urgente, lo priorizado, el resto. La decide el servidor.
          const fa = RANGO_DE_FRANJA[a.franja] ?? 2;
          const fb = RANGO_DE_FRANJA[b.franja] ?? 2;
          if (fa !== fb) return fa - fb;
          const pa = a.posicion_pedida ?? null;
          const pb = b.posicion_pedida ?? null;
          if (pa !== null && pb !== null && pa !== pb) return pa - pb;
          if (pa !== null && pb === null) return -1;
          if (pb !== null && pa === null) return 1;
        }
        const ha = a.fecha_hasta || '';
        const hb = b.fecha_hasta || '';
        if (ha && hb) return ha.localeCompare(hb);
        if (ha) return -1;
        if (hb) return 1;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
      setTickets(dedup);
    } catch (err) {
      console.error(err);
    } finally {
      setTicketsLoaded(true);
    }
  };

  const selectTicket = async (ticket) => {
    const ticketCapsuleId = ticket?.capsule_id || queryCapsuleIds[0];
    try {
      const detailRes = await ticketsAPI.get(tenantId, ticketCapsuleId, ticket.ticket_id);
      setSelectedTicket(detailRes.data);
    } catch {
      setSelectedTicket(ticket);
    }
  };

  useEffect(() => {
    if (!tenantId || !queryCapsuleIds.length || !ticketsLoaded || deepLinkHandled.current) return;
    const ticketId = searchParams.get('ticket');
    if (!ticketId) return;

    const capsuleId = searchParams.get('capsule') || queryCapsuleIds[0];
    const taskId = searchParams.get('task');
    const tab = searchParams.get('tab') || (taskId ? 'board' : 'detail');

    deepLinkHandled.current = true;

    (async () => {
      const fromList = tickets.find((t) => t.ticket_id === ticketId);
      await selectTicket(fromList || { ticket_id: ticketId, capsule_id: capsuleId });
      setActiveTab(tab);
      if (taskId) setHighlightTaskId(taskId);
      setSearchParams({}, { replace: true });
    })();
  }, [tenantId, queryCapsuleIds, ticketsLoaded, tickets, searchParams, setSearchParams]);

  const refreshSelectedTicketDetail = async () => {
    if (!selectedTicket) return;
    const ticketCapsuleId = selectedTicket?.capsule_id || queryCapsuleIds[0];
    try {
      const detailRes = await ticketsAPI.get(tenantId, ticketCapsuleId, selectedTicket.ticket_id);
      setSelectedTicket(detailRes.data);
    } catch (e) {
      // **Un 404 acá es un caso que ya no está**, y seguir mostrando su ficha es mostrar algo que
      // no existe: cada pestaña del detalle sigue pidiendo sus datos y cada una da 404. Se suelta
      // la selección y se dice por qué; el resto de los errores se ignoran, que es lo que
      // corresponde a un refresco de fondo.
      if (e?.response?.status === 404) {
        setSelectedTicket(null);
        setSearchParams((p) => {
          const n = new URLSearchParams(p);
          n.delete('ticket');
          return n;
        }, { replace: true });
        toast.info('Ese caso ya no existe: lo borraron mientras lo tenías abierto');
        await loadTickets();
      }
    }
  };

  useEffect(() => {
    if (!tenantId) return;
    teamAPI.list().then((r) => setTeamMembersTicket(r.data || [])).catch(() => setTeamMembersTicket([]));
  }, [tenantId]);

  // Mis roles en la capsula del caso. Salen de la misma lista de equipo que llena el
  // desplegable de traspaso (`personas.roles`), asi que no hace falta una llamada nueva.
  const misRolesEn = (capsuleId) => {
    const yo = String(user?.email || '').trim().toLowerCase();
    const fila = (teamMembersTicket || []).find(
      (m) => String(m.user_email || '').trim().toLowerCase() === yo
    );
    return (fila?.roles || [])
      .filter((r) => r?.capsule_id === capsuleId)
      .map((r) => String(r.role_id || '').trim());
  };

  // Las dos mitades de `responsabilidad.puede()` para propiedad, replicadas aca por la misma
  // razon que `canEditClassification`: un boton que siempre termina en 403 no ensena la regla,
  // ensena que la aplicacion falla. El backend sigue siendo el que decide — esto solo evita
  // ofrecer lo que va a rechazar.
  const puedoTomar = Boolean(
    selectedTicket
    && !selectedTicket?.propietario?.id
    && (mandaSobreElArea
        || misRolesEn(selectedTicket?.capsule_id).includes(String(selectedTicket?.rol_propietario || '')))
  );
  const puedoTraspasar = Boolean(
    selectedTicket && (mandaSobreElArea || esPropietarioDelCaso(selectedTicket))
  );

  /** El nombre del rol, o su id si el catalogo no lo tiene (un rol borrado sigue en casos viejos). */
  const rolLabel = (roles, id) => {
    if (!id) return '';
    const r = (roles || []).find((x) => x.id === id);
    return r?.name || id;
  };

  const handleTomarTicket = async () => {
    if (!selectedTicket) return;
    setOwnerBusy(true);
    try {
      await ticketsAPI.tomar(tenantId, selectedTicket.capsule_id, selectedTicket.ticket_id);
      toast.success('El caso es tuyo');
      await refreshSelectedTicketDetail();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOwnerBusy(false); }
  };

  const handleTraspasar = async () => {
    if (!selectedTicket || !traspasoEmail) return;
    setOwnerBusy(true);
    try {
      await ticketsAPI.traspasar(tenantId, selectedTicket.capsule_id, selectedTicket.ticket_id, {
        email: traspasoEmail,
        rol: traspasoRol || null,
        motivo: traspasoMotivo,
      });
      toast.success('Caso traspasado');
      setTraspasoEmail(''); setTraspasoRol(''); setTraspasoMotivo('');
      await refreshSelectedTicketDetail();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setOwnerBusy(false); }
  };

  const handleSaveTicketRoutingMetadata = async () => {
    if (!selectedTicket) return;
    const ticketCapsuleId = selectedTicket?.capsule_id || queryCapsuleIds[0];
    setTmSaving(true);
    try {
      await ticketsAPI.patchRoutingMetadata(tenantId, ticketCapsuleId, selectedTicket.ticket_id, {
        intent: tmIntent || null,
        category: tmCategory || null,
        labels: tmLabels,
        priority: tmPriority || null,
        requesting_department: tmDepartment || null,
        sync_conversations: true,
      });
      toast.success('Metadatos del ticket actualizados');
      await refreshSelectedTicketDetail();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTmSaving(false);
    }
  };

  const refreshArtifacts = () => setWorkspaceRefreshToken((t) => t + 1);

  const requestConfirm = ({ title, description, confirmText, onConfirm }) => {
    setConfirmState({ open: true, title, description, confirmText: confirmText || 'Confirmar', onConfirm });
  };

  const handleSaveTicketDetail = async () => {
    if (!selectedTicket) return;
    const ticketCapsuleId = selectedTicket?.capsule_id || queryCapsuleIds[0];
    setDetailSaving(true);
    try {
      await ticketsAPI.patch(tenantId, ticketCapsuleId, selectedTicket.ticket_id, {
        title: detailDraft.title,
        description: detailDraft.description,
        objective: detailDraft.objective,
        reporter: {
          name: detailDraft.reporter.name,
          email: detailDraft.reporter.email || null,
        },
        contact_id: detailDraft.reporter.contact_id || null,
      });
      toast.success('Ticket actualizado');
      setDetailEditing(false);
      await refreshSelectedTicketDetail();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDetailSaving(false);
    }
  };

  const handleCambiarSolicitante = async (contactId, motivo) => {
    if (!selectedTicket) return;
    const ticketCapsuleId = selectedTicket?.capsule_id || queryCapsuleIds[0];
    try {
      const res = await ticketsAPI.setSolicitante(
        tenantId, ticketCapsuleId, selectedTicket.ticket_id, contactId, motivo,
      );
      (res.data?.avisos || []).forEach((a) => toast.warning(a));
      toast.success('Solicitante cambiado');
      await refreshSelectedTicketDetail();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  /**
   * **Propone el expediente; no lo pisa.**
   *
   * Esto sobrescribia titulo, objetivo y descripcion del caso con prosa derivada por un LLM, a un
   * clic, sin diff y sin vuelta atras. El `title` del boton decia *"No modifica archivos del
   * workspace"* —cierto— y no decia que si pisaba lo que alguien habia escrito a mano.
   *
   * La salida no fue un dialogo de confirmacion sino **sacar la irreversibilidad**: la propuesta
   * cae en el formulario de la ficha, que ya existe, con el editor abierto. Quien decide guarda o
   * cancela, y ve lo que habia al lado de lo que se propone. Un diff que se confirma todavia
   * escribe de golpe; esto no escribe nada hasta que una persona apreta Guardar.
   */
  const handleRefreshNarrative = async () => {
    if (!selectedTicket) return;
    const ticketCapsuleId = selectedTicket?.capsule_id || queryCapsuleIds[0];
    setNarrativeRefreshing(true);
    try {
      const res = await ticketsAPI.refreshNarrative(
        tenantId, ticketCapsuleId, selectedTicket.ticket_id, { proponer: true },
      );
      const p = res.data || {};
      if (!p.objective && !p.title && !p.description) {
        toast.info('No se pudo armar una propuesta con los hilos que hay');
        return;
      }
      setDetailDraft((d) => ({
        ...d,
        ...(p.title != null ? { title: p.title } : {}),
        ...(p.objective != null ? { objective: p.objective } : {}),
        ...(p.description != null ? { description: p.description } : {}),
      }));
      setDetailEditing(true);
      toast.info('Propuesta cargada en el formulario. Revisala y guardá, o cancelá.',
        { duration: 6000 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setNarrativeRefreshing(false);
    }
  };

  const handleCreate = async () => {
    const targetCapsuleId = createCapsuleId || queryCapsuleIds[0];
    if (!targetCapsuleId) {
      toast.error('No hay capsula accesible para crear el ticket');
      return;
    }
    if (!newTicket.title.trim()) {
      toast.error('El titulo es obligatorio');
      return;
    }
    setCreating(true);
    try {
      await ticketsAPI.create(tenantId, targetCapsuleId, {
        title: newTicket.title,
        description: newTicket.description,
        priority: newTicket.priority,
        intent: createIntent || undefined,
        category: createCategory || undefined,
        requesting_department: createDepartment || undefined,
        workflow_template_id: createApplyWorkflow ? (createTemplateId || undefined) : undefined,
        apply_workflow: createApplyWorkflow,
        reporter: {
          name: newTicket.reporter.name,
          email: newTicket.reporter.email || null,
        },
      });
      setCreateOpen(false);
      setNewTicket({ title: '', description: '', priority: 'medium', reporter: { name: '', email: '', contact_id: null } });
      setCreateIntent('');
      setCreateCategory('');
      setCreateDepartment('');
      setCreateTemplateId('');
      setCreateApplyWorkflow(true);
      loadTickets();
      toast.success('Ticket creado');
    } catch (err) {
      console.error(err);
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  /**
   * Cambiar de estado, pidiendo la resolución cuando la cápsula la exige.
   *
   * **La pregunta se hace antes de llamar**, mirando la bandera del catálogo, para que la persona
   * no se choque con un 400. Pero el backend la exige igual: la guarda de verdad está allá, y
   * ésta es sólo para no hacerla adivinar.
   */
  const [pidiendoResolucion, setPidiendoResolucion] = useState('');
  const [guardandoResolucion, setGuardandoResolucion] = useState(false);

  const handleStatusChange = async (status, resolucion) => {
    if (!selectedTicket) return;
    const destino = (capsuleTicketStatuses || []).find((s) => s.id === status);
    const yaEscrita = String(selectedTicket.resolucion || '').trim();
    if (destino?.exige_resolucion && !resolucion && !yaEscrita) {
      setPidiendoResolucion(status);
      return;
    }

    const ticketCapsuleId = selectedTicket?.capsule_id || queryCapsuleIds[0];
    setGuardandoResolucion(true);
    try {
      await ticketsAPI.updateStatus(tenantId, ticketCapsuleId, selectedTicket.ticket_id, status, resolucion);
      const res = await ticketsAPI.get(tenantId, ticketCapsuleId, selectedTicket.ticket_id);
      setSelectedTicket(res.data);
      setPidiendoResolucion('');
      loadTickets();
      toast.success(`Estado cambiado a ${status}`);
    } catch (err) {
      // **El motivo se muestra.** Hasta hoy este `catch` era pelado y decía «Error al cambiar
      // estado» para todo: el 400 que explica qué falta no llegaba nunca a la persona que tenía
      // que leerlo, y el que más importa es justamente el que se puede accionar.
      toast.error(formatApiError(err) || 'Error al cambiar estado');
    } finally {
      setGuardandoResolucion(false);
    }
  };

  const handleDeleteTicket = async () => {
    if (!selectedTicket) return;
    setConfirmState({
      open: true,
      title: 'Eliminar ticket',
      description: `Se eliminará el ticket ${selectedTicket.ticket_id} con hilos vinculados, workspace (.md/artifacts) y KB Tickets/${selectedTicket.ticket_id}/. Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      onConfirm: async () => {
        const ticketCapsuleId = selectedTicket?.capsule_id || queryCapsuleIds[0];
        try {
          await ticketsAPI.delete(tenantId, ticketCapsuleId, selectedTicket.ticket_id);
          toast.success('Ticket eliminado');
          setSelectedTicket(null);
          await loadTickets();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'No se pudo eliminar el ticket');
        }
      },
    });
  };

  const handleDeleteTicketFromList = async (ticket) => {
    if (!ticket) return;
    setConfirmState({
      open: true,
      title: 'Eliminar ticket',
      description: `Se eliminará el ticket ${ticket.ticket_id} con hilos vinculados, workspace y KB del ticket. Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      onConfirm: async () => {
        const ticketCapsuleId = ticket?.capsule_id || queryCapsuleIds[0];
        try {
          await ticketsAPI.delete(tenantId, ticketCapsuleId, ticket.ticket_id);
          toast.success('Ticket eliminado');
          if (selectedTicket?.ticket_id === ticket.ticket_id) {
            setSelectedTicket(null);
          }
          await loadTickets();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'No se pudo eliminar el ticket');
        }
      },
    });
  };

  const handlePurgeAllTickets = async () => {
    if (!tenantId || purgePhrase.trim() !== PURGE_TICKETS_PHRASE) return;
    setPurgeLoading(true);
    try {
      const res = await ticketsAPI.purgeAll(tenantId, { confirmation: purgePhrase.trim() });
      toast.success(`Eliminados ${res.data?.tickets_deleted ?? 0} tickets (${res.data?.conversations_deleted ?? 0} hilos)`);
      setPurgeOpen(false);
      setPurgePhrase('');
      setSelectedTicket(null);
      await loadTickets();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPurgeLoading(false);
    }
  };

  const handleOrphanScrub = async () => {
    if (!tenantId || scrubPhrase.trim() !== SCRUB_PHRASE) return;
    setScrubLoading(true);
    try {
      const res = await ticketsAPI.orphanScrub(tenantId, { confirmation: scrubPhrase.trim(), dry_run: false });
      const del = res.data?.deleted || {};
      const n = Object.values(del).reduce((a, b) => a + (Number(b) || 0), 0);
      toast.success(`Scrub OK: ${n} docs huérfanos · Qdrant ${res.data?.qdrant_points_removed ?? 0}`);
      setScrubOpen(false);
      setScrubPhrase('');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setScrubLoading(false);
    }
  };

  // **Ya no se filtra en el cliente.** Lo que llega viene filtrado por el servidor, con `q` y
  // `duenio` entre los parametros. Volver a filtrar aca con el texto a medio tipear haria
  // desaparecer filas que si matchean la busqueda aplicada.
  const filtered = tickets;

  return (
    <div className="flex h-full" data-testid="tickets-page">
      {/* Ticket list sidebar */}
      <div className="w-80 border-r border-brand-border bg-white flex flex-col">
        <div className="h-14 flex items-center justify-between px-4 border-b border-brand-border">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-700">Tickets</span>
          <div className="flex items-center gap-1">
            {isAdmin && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-[10px] text-amber-700 hover:bg-amber-50"
                  onClick={() => setScrubOpen(true)}
                  data-testid="orphan-scrub-tickets"
                  title="Limpiar huérfanos"
                >
                  Huérfanos
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setPurgeOpen(true)}
                  data-testid="purge-all-tickets"
                >
                  Vaciar tickets
                </Button>
              </>
            )}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="create-ticket-button" className="h-8 w-8">
                  <Plus className="w-4 h-4" />
                </Button>
              </DialogTrigger>
            <DialogContent className="rounded-sm border-brand-border">
              <DialogHeader>
                <DialogTitle className="font-heading text-lg">Nuevo Ticket</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em]">Titulo</Label>
                  <Input data-testid="ticket-title-input" value={newTicket.title} onChange={e => setNewTicket(p => ({ ...p, title: e.target.value }))} className="rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em]">Descripcion</Label>
                  <textarea data-testid="ticket-desc-input" value={newTicket.description} onChange={e => setNewTicket(p => ({ ...p, description: e.target.value }))} className="w-full border border-brand-border rounded-sm p-2 text-sm h-20 focus:ring-2 focus:ring-brand-primary focus:outline-none" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em]">Reportado por</Label>
                  <ContactPicker
                    testId="ticket-reporter-input"
                    value={newTicket.reporter}
                    onChange={(rep) => setNewTicket((p) => ({ ...p, reporter: { ...p.reporter, ...rep } }))}
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em]">Email</Label>
                  <Input
                    data-testid="ticket-reporter-email-input"
                    value={newTicket.reporter.email || ''}
                    onChange={(e) => setNewTicket((p) => ({ ...p, reporter: { ...p.reporter, email: e.target.value } }))}
                    placeholder="email@empresa.com"
                    className="rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em]">Capsula</Label>
                  <select
                    data-testid="ticket-capsule-select"
                    value={createCapsuleId || queryCapsuleIds[0] || ''}
                    onChange={(e) => setCreateCapsuleId(e.target.value)}
                    className="w-full border border-brand-border rounded-sm h-9 px-2 text-sm bg-white"
                  >
                    {(accessibleCapsules.length ? accessibleCapsules.filter((c) => queryCapsuleIds.includes(c.capsule_id)) : queryCapsuleIds.map((id) => ({ capsule_id: id, name: id }))).map((c) => (
                      <option key={c.capsule_id} value={c.capsule_id}>{c.name || c.capsule_id}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em]">Intent</Label>
                  <select
                    data-testid="ticket-intent-select"
                    value={createIntent}
                    onChange={(e) => setCreateIntent(e.target.value)}
                    className="w-full border border-brand-border rounded-sm h-9 px-2 text-sm bg-white"
                  >
                    <option value="">— Sin intent —</option>
                    {createIntents.filter((i) => i?.id).map((i) => (
                      <option key={i.id} value={i.id}>{i.name || i.id}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em]">Categoria</Label>
                  <select
                    data-testid="ticket-category-select"
                    value={createCategory}
                    onChange={(e) => setCreateCategory(e.target.value)}
                    className="w-full border border-brand-border rounded-sm h-9 px-2 text-sm bg-white"
                  >
                    <option value="">— Sin categoria —</option>
                    {createCategories.filter((c) => c?.id).map((c) => (
                      <option key={c.id} value={c.id}>{c.name || c.id}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em]">Gerencia</Label>
                  <select
                    data-testid="ticket-department-select"
                    value={createDepartment}
                    onChange={(e) => setCreateDepartment(e.target.value)}
                    className="w-full border border-brand-border rounded-sm h-9 px-2 text-sm bg-white"
                  >
                    <option value="">— Sin gerencia —</option>
                    {createDepartments.filter((d) => d?.id || d?.name).map((d) => (
                      <option key={d.id || d.name} value={d.id || d.name}>{d.name || d.id}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em]">Plantilla de trabajo</Label>
                  <select
                    data-testid="ticket-template-select"
                    value={createTemplateId}
                    onChange={(e) => setCreateTemplateId(e.target.value)}
                    disabled={!createApplyWorkflow}
                    className="w-full border border-brand-border rounded-sm h-9 px-2 text-sm bg-white disabled:opacity-50"
                  >
                    <option value="">Auto (segun intent)</option>
                    {createTemplates.map((t) => (
                      <option key={t.template_id} value={t.template_id}>{t.name || t.template_id}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-2 mt-2 text-xs text-zinc-600">
                    <input type="checkbox" checked={createApplyWorkflow} onChange={(e) => setCreateApplyWorkflow(e.target.checked)} />
                    Aplicar plantilla al crear
                  </label>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em]">Prioridad</Label>
                  <select
                    value={newTicket.priority}
                    onChange={(e) => setNewTicket((p) => ({ ...p, priority: e.target.value }))}
                    className="w-full border border-brand-border rounded-sm h-9 px-2 text-sm bg-white"
                  >
                    {(createPriorities.length
                      ? createPriorities.filter((p) => p?.id)
                      : [{ id: 'low', name: 'Baja' }, { id: 'medium', name: 'Media' }, { id: 'high', name: 'Alta' }, { id: 'critical', name: 'Critica' }]
                    ).map((p) => (
                      <option key={p.id} value={p.id}>{p.name || p.id}</option>
                    ))}
                  </select>
                </div>
                <Button data-testid="ticket-submit-button" onClick={handleCreate} disabled={creating} className="w-full bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Crear Ticket'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>
        <div className="px-3 py-2 border-b border-brand-border space-y-1.5">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400" />
            <Input data-testid="ticket-search" value={filter}
              onChange={e => setFilter(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') setBusquedaAplicada(filter.trim());
                if (e.key === 'Escape') { setFilter(''); setBusquedaAplicada(''); }
              }}
              placeholder="Buscar por numero o titulo, y Enter" className="h-8 pl-7 text-xs rounded-sm" />
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <select
              data-testid="filtro-estado" value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="h-7 text-[10px] border border-brand-border rounded-sm px-1 bg-white"
            >
              <option value="">Todos los estados</option>
              <option value="abiertos">Solo abiertos</option>
              {capsuleTicketStatuses.filter((x) => x?.id).map((x) => (
                <option key={x.id} value={x.id}>{x.name || x.id}</option>
              ))}
            </select>
            <select
              data-testid="filtro-prioridad" value={filtroPrioridad}
              onChange={(e) => setFiltroPrioridad(e.target.value)}
              className="h-7 text-[10px] border border-brand-border rounded-sm px-1 bg-white"
            >
              <option value="">Toda prioridad</option>
              {capsulePriorities.filter((x) => x?.id).map((x) => (
                <option key={x.id} value={x.id}>{x.name || x.id}</option>
              ))}
            </select>
            <select
              data-testid="filtro-vence" value={filtroVence}
              onChange={(e) => setFiltroVence(e.target.value)}
              className="h-7 text-[10px] border border-brand-border rounded-sm px-1 bg-white"
            >
              <option value="">Cualquier fecha</option>
              <option value="vencidos">Vencidos</option>
              <option value="hoy">Vencen hoy</option>
            </select>
            <select
              data-testid="orden-bandeja" value={orden}
              onChange={(e) => setOrden(e.target.value)}
              title="Por prioridad: arriba lo urgente (vencido, por vencer o crítico), después la prioridad de la empresa que arma Dirección, y al final el resto por fecha comprometida."
              className="h-7 text-[10px] border border-brand-border rounded-sm px-1 bg-white"
            >
              <option value="">Por fecha comprometida</option>
              <option value="prioridad">Por prioridad</option>
            </select>
            <select
              data-testid="filtro-duenio" value={filtroDuenio}
              onChange={(e) => setFiltroDuenio(e.target.value)}
              className="h-7 text-[10px] border border-brand-border rounded-sm px-1 bg-white"
            >
              <option value="">Con dueño o sin el</option>
              <option value="sin_asignar">Sin dueño (la cola)</option>
              <option value="__mios__">Los mios</option>
            </select>
            <select
              data-testid="filtro-gerencia" value={filtroGerencia}
              onChange={(e) => setFiltroGerencia(e.target.value)}
              className="h-7 text-[10px] border border-brand-border rounded-sm px-1 bg-white"
            >
              <option value="">Toda gerencia</option>
              {capsuleDepartments.filter((x) => x?.id).map((x) => (
                <option key={x.id} value={x.id}>{x.name || x.id}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          {filtered.map((t, i) => (
            <Fragment key={t.ticket_id}>
            {/* **Las tres franjas de la bandeja por prioridad, rotuladas** (2026-10-09). Sin el rótulo,
                un caso vencido arriba de lo que priorizó Dirección parece un error del orden. */}
            {orden === 'prioridad' && t.franja && (i === 0 || filtered[i - 1].franja !== t.franja) && (
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider bg-zinc-50 border-b border-brand-border text-zinc-500" data-testid={`franja-${t.franja}`}>
                {FRANJAS[t.franja] || t.franja}
              </div>
            )}
            <div
              key={t.ticket_id}
              data-testid={`ticket-item-${t.ticket_id}`}
              onClick={() => selectTicket(t)}
              className={`w-full text-left px-3 py-3 border-b border-brand-border transition-colors duration-150 cursor-pointer ${
                selectedTicket?.ticket_id === t.ticket_id ? 'bg-brand-surface' : 'hover:bg-zinc-50'
              }`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') selectTicket(t); }}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-mono font-semibold text-brand-primary">{t.ticket_id}</span>
                <div className="flex items-center gap-1">
                  {/* Borra el caso con su workspace, sus artefactos y sus conversaciones
                      vinculadas. **Si** tiene confirmacion —el dialogo de abajo— pero no tenia
                      `data-testid` ni `aria-label`, y esta en el borde superior de cada fila de una
                      lista con scroll: el gesto de apuntar a una fila pasa por arriba de el. */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDeleteTicketFromList(t); }}
                    className="text-zinc-300 hover:text-red-600"
                    title="Eliminar el caso, su workspace y sus conversaciones vinculadas"
                    aria-label="Eliminar el caso"
                    data-testid={`borrar-caso-${t.ticket_id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                  <Badge variant="outline" className="text-[10px] rounded-sm px-1 py-0">{t.capsule_id || '—'}</Badge>
                  {t.intent && (
                    <Badge variant="secondary" className="text-[10px] rounded-sm px-1 py-0 max-w-[100px] truncate" title={intentName(t.intent)}>
                      {intentName(t.intent)}
                    </Badge>
                  )}
                  <Badge className={`text-[10px] rounded-sm px-1 py-0 ${priorityColors[t.priority] || ''}`}>
                    {(capsulePriorities.find((p) => p.id === t.priority)?.name) || t.priority}
                  </Badge>
                </div>
              </div>
              <p className="text-xs font-medium text-[#09090B] truncate">{(t.title || '').replace(/^\[[A-Z0-9_]+\]\s*/i, '')}</p>
              {/* Quien lo tiene. Va en su propia linea y no apretado con el semaforo: con un nombre
                  largo los tres se aplastaban dentro de 320 px de sidebar. */}
              {(() => {
                const d = duenioDelCaso(t);
                return (
                  <p
                    className={`text-[10px] truncate mt-0.5 ${d.sinDuenio ? 'text-amber-700' : 'text-zinc-500'}`}
                    data-testid={`ticket-duenio-${t.ticket_id}`}
                    title={d.texto}
                  >
                    {d.texto}
                  </p>
                );
              })()}
              {/* **El reloj que le corre a quien atiende.** La cola pintaba solo el semáforo del
                  compromiso —cuándo hay que tenerlo resuelto— y en soporte lo primero que corre es
                  el otro: cuánto hace que quien pidió no recibe señales de vida. Ese reloj existía y
                  se mostraba en el tablero ejecutivo, que un `operator` no ve: la persona que podía
                  contestar era la única que no veía el reloj que le corría.

                  Sólo aparece en los que **todavía esperan**: un caso ya atendido no tiene nada que
                  avisar, y una marca de más desgasta la marca. */}
              {t.atencion && (
                <p
                  className={`text-[10px] truncate mt-0.5 ${
                    t.atencion.semaforo === 'breach' ? 'text-brand-danger font-medium'
                      : t.atencion.semaforo === 'risk' ? 'text-amber-700' : 'text-zinc-500'
                  }`}
                  data-testid={`ticket-atencion-${t.ticket_id}`}
                  title={t.atencion.modo === 'estado'
                    ? 'Horas hábiles que lleva esperando que el caso entre en un estado que cuente como atendido'
                    : 'Horas hábiles que lleva esperando una respuesta de una persona'}
                >
                  sin responder hace {t.atencion.horas_esperando} h
                  {t.atencion.sla_horas ? ` de ${t.atencion.sla_horas} h` : ' · sin reloj'}
                </p>
              )}
              <div className="flex items-center justify-between mt-1">
                <Badge variant="outline" className={`text-[10px] rounded-sm px-1 py-0 ${statusColors[t.status] || ''}`}>{statusName(t.status)}</Badge>
                <SemaforoDelCaso caso={t} />
                <EsperaAAlguienQueNoEsta caso={t} />
              </div>
            </div>
            </Fragment>
          ))}
        </div>
      </div>

      {/* Main area - detail or chat */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {!selectedTicket ? (
          <div className="flex items-center justify-center h-full text-zinc-400">
            <p className="text-sm">Selecciona un ticket para ver los detalles</p>
          </div>
        ) : (
          <>
            {/* Header with tabs */}
            <div className="border-b border-brand-border bg-white px-4">
              <div className="flex items-center justify-between py-2">
                <div>
                  <h2 className="font-heading text-base font-bold text-[#09090B]">
                    {(selectedTicket.title || '').replace(/^\[[A-Z0-9_]+\]\s*/i, '')}
                  </h2>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-[11px] font-mono font-semibold text-brand-primary">{selectedTicket.ticket_id}</span>
                    {selectedTicket.ticket_name && (
                      <span className="text-[10px] font-mono text-zinc-400">{selectedTicket.ticket_name}</span>
                    )}
                    {selectedTicket.intent && (
                      <Badge variant="secondary" className="text-[10px] rounded-sm" data-testid="ticket-intent-badge">
                        {selectedTicket.intent}
                      </Badge>
                    )}
                    <Badge variant="outline" className={`text-[10px] rounded-sm ${statusColors[selectedTicket.status]}`}>{statusName(selectedTicket.status)}</Badge>
                    <Badge
                      className={`text-[10px] rounded-sm ${!priorityStyle(selectedTicket.priority) ? (priorityColors[selectedTicket.priority] || '') : ''}`}
                      style={priorityStyle(selectedTicket.priority)}
                    >
                      {priorityName(selectedTicket.priority)}
                    </Badge>
                    {canEditClassification && (
                      <select
                        data-testid="ticket-header-priority-select"
                        className="text-xs border border-brand-border rounded-sm px-2 py-0.5 h-6"
                        value={tmPriority || selectedTicket.priority || 'medium'}
                        onChange={async (e) => {
                          const next = e.target.value;
                          setTmPriority(next);
                          try {
                            await ticketsAPI.patchRoutingMetadata(
                              tenantId,
                              selectedTicket.capsule_id || queryCapsuleIds[0],
                              selectedTicket.ticket_id,
                              { priority: next, sync_conversations: true },
                            );
                            setSelectedTicket((t) => ({ ...t, priority: next }));
                            toast.success('Prioridad actualizada');
                            loadTickets();
                          } catch (err) {
                            toast.error(formatApiError(err));
                          }
                        }}
                      >
                        {(capsulePriorities.length
                          ? capsulePriorities.filter((p) => p?.id)
                          : [{ id: 'low', name: 'Baja' }, { id: 'medium', name: 'Media' }, { id: 'high', name: 'Alta' }, { id: 'critical', name: 'Critica' }]
                        ).map((p) => (
                          <option key={p.id} value={p.id}>{p.name || p.id}</option>
                        ))}
                      </select>
                    )}
                    <MarcaDeConformidad valor={selectedTicket.conformidad} />
                    {selectedTicket.created_at && (
                      <span className="text-[10px] text-zinc-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {fechaYHora(selectedTicket.created_at)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/*
                    El primer lugar de esta fila era «Eliminar», en rojo lleno: la accion
                    irreversible estaba donde la mano va sola, y la que casi siempre
                    corresponde —tomar el caso— vivia enterrada en una tarjeta adentro de la
                    pestaña Detalle. Un caso sin dueño es justo el que se duerme, y la
                    pantalla era la que menos empujaba a resolverlo.
                  */}
                  {puedoTomar && (
                    <Button
                      size="sm"
                      disabled={ownerBusy}
                      onClick={handleTomarTicket}
                      data-testid="btn-tomar-ticket"
                      className="rounded-sm bg-brand-primary hover:bg-[#001F6D] text-white text-xs h-7"
                    >
                      {ownerBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Tomar el caso'}
                    </Button>
                  )}
                  <select
                    data-testid="ticket-status-select"
                    value={selectedTicket.status}
                    onChange={e => handleStatusChange(e.target.value)}
                    className="text-xs border border-brand-border rounded-sm px-2 py-1 h-7"
                  >
                    {/*
                      Sin fallback a los ocho ids de plataforma: en una cápsula con estados
                      propios ese respaldo ofrecía estados que el backend rechaza, y el
                      usuario se encontraba con un 400 sin entender por qué. Si el catálogo
                      no cargó, se muestra sólo el estado actual y se dice que falta.
                    */}
                    {(capsuleTicketStatuses.length
                      ? capsuleTicketStatuses
                      : [{ id: selectedTicket.status, name: `${selectedTicket.status} (catalogo sin cargar)` }]
                    ).map((s) => {
                      /* **Un estado que el backend va a rechazar no se ofrece**, y es la misma
                         lección del comentario de arriba una vuelta más adelante: a CERRADO no se
                         llega a mano —lo llega la calificación de quien pidió, o el cierre
                         automático—. Ofrecerlo y contestar 400 deja a la persona apretando un
                         estado que parece disponible.

                         Y **no se esconde**: se muestra deshabilitado diciendo por qué. Que el
                         estado existe es información —el caso va a terminar ahí— y una opción que
                         desaparece se lee como que el catálogo está mal. Si ya calificó, se puede
                         elegir: reabrir y volver a cerrar no tiene que pedir la nota otra vez. */
                      const laCierraLaCalificacion = Boolean(
                        s.exige_conformidad && !selectedTicket.conformidad?.valor,
                      );
                      return (
                        <option key={s.id} value={s.id} disabled={laCierraLaCalificacion}>
                          {s.name || s.id}
                          {laCierraLaCalificacion ? ' — lo cierra la calificación' : ''}
                        </option>
                      );
                    })}
                  </select>
                  <PedirLaResolucion
                    abierto={!!pidiendoResolucion}
                    estado={(capsuleTicketStatuses || []).find((s) => s.id === pidiendoResolucion)?.name || pidiendoResolucion}
                    guardando={guardandoResolucion}
                    onCancelar={() => setPidiendoResolucion('')}
                    onConfirmar={(texto) => handleStatusChange(pidiendoResolucion, texto)}
                  />
                  {/*
                    Eliminar, ultimo y apagado. La auditoria proponia mandarlo a un menu de
                    acciones secundarias; se midio y no valia el precio: ninguna primitiva de
                    menu de Radix esta hoy en el bundle —el producto usa `select` nativos— asi
                    que el primer menu costaba **16,21 kB gzip** sobre un bundle que el build
                    ya marca como grande, para un menu de un solo item.

                    Lo que la auditoria persigue es que la accion irreversible deje de ser la
                    mas visible ("primero de la fila, en rojo lleno, donde la mano va sola"), y
                    eso se consigue con la posicion y el peso visual: va ultimo, sin relleno y
                    en gris, y solo se pone rojo al pasar por encima. Los pasos hasta borrar no
                    cambian: sigue estando el ConfirmActionDialog.

                    Si el dia de mañana aparecen mas acciones secundarias —archivar, duplicar,
                    exportar— el menu se paga solo y este boton se muda adentro.
                  */}
                  <Button
                    onClick={handleDeleteTicket}
                    variant="ghost" size="sm"
                    data-testid="ticket-eliminar"
                    title="Eliminar el caso"
                    className="rounded-sm text-zinc-400 hover:text-red-600 hover:bg-red-50 text-xs h-7 px-2"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {/*
                Arriba de las pestañas y no adentro de una: la pregunta "¿hay alguien
                trabajando en esto?" no pertenece a Detalle ni a Chat — se hace apenas se abre
                el caso, y la respuesta cambia lo que la persona hace a continuación.
              */}
              <div className="px-1 pb-2">
                <ActividadAgentica
                  tenantId={tenantId}
                  capsuleId={selectedTicket.capsule_id || queryCapsuleIds[0]}
                  ticketId={selectedTicket.ticket_id}
                />
              </div>

              {/* **La fecha comprometida va arriba de las pestañas** (2026-10-09): estaba en letra
                  chica a mitad del Detalle y no aparecía en el Tablero, que es donde están las
                  fechas del plan. Es la misma pieza que en la bandeja y en Mis solicitudes. */}
              <div className="px-1 pb-2">
                <FechaComprometida
                  caso={selectedTicket}
                  acciones={selectedTicket.puedo?.planificar ? (
                    <MoverElCompromiso
                      tenantId={tenantId}
                      capsuleId={selectedTicket.capsule_id || queryCapsuleIds[0]}
                      ticket={selectedTicket}
                      onHecho={refreshSelectedTicketDetail}
                    />
                  ) : null}
                />
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-transparent h-8 gap-0 p-0">
                  <TabsTrigger value="detail" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-primary h-8 text-xs px-4">
                    Detalle
                  </TabsTrigger>
                  <TabsTrigger value="chat" data-testid="ticket-chat-tab" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-primary h-8 text-xs px-4 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Asistente IA
                  </TabsTrigger>
                  <TabsTrigger value="conversation" data-testid="ticket-conversation-tab" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-primary h-8 text-xs px-4 flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" /> Conversacion
                  </TabsTrigger>
                  <TabsTrigger value="reply" data-testid="ticket-reply-tab" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-primary h-8 text-xs px-4 flex items-center gap-1">
                    <Reply className="w-3 h-3" /> Responder
                  </TabsTrigger>
                  <TabsTrigger value="board" data-testid="ticket-board-tab" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-primary h-8 text-xs px-4 flex items-center gap-1">
                    <LayoutGrid className="w-3 h-3" /> Tablero
                  </TabsTrigger>
                  <TabsTrigger value="memory" data-testid="ticket-memory-tab" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-primary h-8 text-xs px-4 flex items-center gap-1">
                    <BookOpen className="w-3 h-3" /> Memoria
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-hidden">
              {activeTab === 'detail' && (
                <div className="h-full overflow-auto p-4 space-y-4" data-testid="ticket-detail">
                  {/* Description */}
                  <Card className="border border-brand-border rounded-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Detalle del ticket</span>
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            disabled={narrativeRefreshing}
                            onClick={handleRefreshNarrative}
                            title="Arma una propuesta de título, objetivo y descripción desde los hilos y la carga en el formulario. No guarda nada hasta que lo guardes vos."
                            data-testid="refresh-narrative-btn"
                          >
                            {narrativeRefreshing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                            Proponer desde los hilos
                          </Button>
                        {!detailEditing ? (
                          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setDetailEditing(true)} data-testid="edit-ticket-detail-btn">
                            <Pencil className="w-3 h-3 mr-1" /> Editar
                          </Button>
                        ) : (
                          <div className="flex gap-1">
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setDetailEditing(false)}>Cancelar</Button>
                            <Button size="sm" className="h-7 text-xs bg-brand-primary text-white" disabled={detailSaving} onClick={handleSaveTicketDetail} data-testid="save-ticket-detail-btn">
                              {detailSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                              Guardar
                            </Button>
                          </div>
                        )}
                        </div>
                      </div>
                      {detailEditing ? (
                        <div className="space-y-3">
                          <div>
                            <Label className="text-[10px] text-zinc-500">Titulo</Label>
                            <Input className="h-8 text-sm mt-0.5" value={detailDraft.title} onChange={(e) => setDetailDraft((d) => ({ ...d, title: e.target.value }))} data-testid="ticket-edit-title" />
                          </div>
                          <div>
                            <Label className="text-[10px] text-zinc-500">Objetivo</Label>
                            <Textarea className="text-sm mt-0.5 min-h-[60px]" value={detailDraft.objective} onChange={(e) => setDetailDraft((d) => ({ ...d, objective: e.target.value }))} data-testid="ticket-edit-objective" />
                          </div>
                          <div>
                            <Label className="text-[10px] text-zinc-500">Descripcion</Label>
                            <Textarea className="text-sm mt-0.5 min-h-[100px]" value={detailDraft.description} onChange={(e) => setDetailDraft((d) => ({ ...d, description: e.target.value }))} data-testid="ticket-edit-description" />
                          </div>
                          <div>
                            <Label className="text-[10px] text-zinc-500">Reportado por</Label>
                            <ContactPicker
                              value={detailDraft.reporter}
                              onChange={(rep) => setDetailDraft((d) => ({ ...d, reporter: { ...d.reporter, ...rep } }))}
                            />
                            <p className="text-[10px] text-zinc-400 mt-1">
                              De donde salio la captura. Quien ve el caso lo decide el solicitante.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="mb-3">
                            <span className="text-[10px] uppercase tracking-wider text-zinc-500">Objetivo</span>
                            <p className="text-sm text-zinc-800 whitespace-pre-wrap mt-0.5" data-testid="ticket-objective">
                              {selectedTicket.objective || '—'}
                            </p>
                          </div>
                          <div className="mb-3">
                            <span className="text-[10px] uppercase tracking-wider text-zinc-500">Detalle</span>
                            <p className="text-sm text-zinc-600 whitespace-pre-wrap mt-0.5" data-testid="ticket-description">
                              {selectedTicket.description || '—'}
                            </p>
                          </div>
                          {selectedTicket.original_mail_subject && (
                            <div className="mb-3">
                              <span className="text-[10px] uppercase tracking-wider text-zinc-500">Asunto original</span>
                              <p className="text-sm text-zinc-700 mt-0.5">{selectedTicket.original_mail_subject}</p>
                            </div>
                          )}
                          <div className="grid grid-cols-3 gap-4 mt-4 text-xs">
                            <div><span className="text-zinc-500">Tipo:</span> <span className="font-medium">{selectedTicket.type}</span></div>
                            <div><span className="text-zinc-500">Prioridad:</span> <Badge className={`text-xs rounded-sm ${!priorityStyle(selectedTicket.priority) ? (priorityColors[selectedTicket.priority] || '') : ''}`} style={priorityStyle(selectedTicket.priority)}>{priorityName(selectedTicket.priority)}</Badge></div>
                            <div><span className="text-zinc-500">Reportado por:</span> <span className="font-medium">{selectedTicket.reporter?.name || '—'}</span></div>
                          </div>
                          <div className="mt-3 pt-3 border-t border-brand-border flex items-start justify-between gap-3">
                            <div className="text-xs">
                              <span className="text-zinc-500">Solicitante:</span>{' '}
                              <span className="font-medium">{selectedTicket.solicitante?.nombre || '—'}</span>
                              {selectedTicket.solicitante?.email ? (
                                <span className="text-zinc-400"> · {selectedTicket.solicitante.email}</span>
                              ) : (
                                <span className="text-amber-700"> · sin correo: no puede ver el caso</span>
                              )}
                              <p className="text-[10px] text-zinc-400 mt-0.5">
                                Quien lo pidio. Ve el caso en Mis solicitudes, y su jefe tambien.
                              </p>
                            </div>
                            {canEditClassification && (
                              <Button
                                variant="outline" size="sm"
                                className="h-7 text-[11px] rounded-sm shrink-0"
                                onClick={() => setCambiandoSolicitante(true)}
                              >
                                Cambiar
                              </Button>
                            )}
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  {canEditClassification && (
                    <Card className="border border-brand-border rounded-sm" data-testid="ticket-routing-metadata">
                      <CardHeader className="pb-2 border-b border-brand-border py-3">
                        <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em]">Clasificacion (manual)</CardTitle>
                        <p className="text-[10px] text-zinc-500 font-normal normal-case">Intent, categoria, gerencia, etiquetas y prioridad (catalogo de capsula). Se sincroniza a conversaciones vinculadas.</p>
                      </CardHeader>
                      <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                        <div>
                          <Label className="text-[10px] text-zinc-500">Intent</Label>
                          <select className="w-full border border-brand-border rounded-sm h-8 px-2 text-xs mt-0.5" value={tmIntent} onChange={e => setTmIntent(e.target.value)} data-testid="tm-intent-select">
                            <option value="">—</option>
                            {capsuleIntents.filter((i) => i?.id).map((i) => (
                              <option key={i.id} value={i.id}>{i.name || i.id}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-[10px] text-zinc-500">Categoria</Label>
                          <select className="w-full border border-brand-border rounded-sm h-8 px-2 text-xs mt-0.5" value={tmCategory} onChange={e => setTmCategory(e.target.value)} data-testid="tm-category-select">
                            <option value="">—</option>
                            {capsuleCategories.filter((c) => c?.id).map((c) => (
                              <option key={c.id} value={c.id}>{c.name || c.id}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-[10px] text-zinc-500">Gerencia</Label>
                          <select className="w-full border border-brand-border rounded-sm h-8 px-2 text-xs mt-0.5" value={tmDepartment} onChange={e => setTmDepartment(e.target.value)} data-testid="tm-department-select">
                            <option value="">—</option>
                            {capsuleDepartments.filter((d) => d?.id || d?.name).map((d) => (
                              <option key={d.id || d.name} value={d.id || d.name}>{d.name || d.id}</option>
                            ))}
                          </select>
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-[10px] text-zinc-500">Etiquetas</Label>
                          <LabelMultiSelect
                            catalog={capsuleLabels}
                            value={tmLabels}
                            onChange={setTmLabels}
                            testId="ticket-labels-select"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-zinc-500">Prioridad</Label>
                          <select className="w-full border border-brand-border rounded-sm h-8 px-2 text-xs mt-0.5" value={tmPriority} onChange={e => setTmPriority(e.target.value)}>
                            {(capsulePriorities.length
                              ? capsulePriorities.filter((p) => p?.id)
                              : [{ id: 'low' }, { id: 'medium' }, { id: 'high' }, { id: 'critical' }]
                            ).map((p) => (
                              <option key={p.id} value={p.id}>{p.name || p.id}</option>
                            ))}
                          </select>
                        </div>
                        <div className="sm:col-span-2">
                          <Button type="button" size="sm" className="h-8 text-xs bg-brand-primary text-white" disabled={tmSaving} onClick={handleSaveTicketRoutingMetadata}>
                            {tmSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                            Guardar metadatos
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Propietario del caso.

                      Hasta el 2026-08-14 no existia: habia tres nociones de "quien" sueltas
                      —`assigned_to` (muerto), `assignees[]` (sin ninguna pantalla) y el
                      assignee de cada tarea— y ninguna decia quien manda sobre el caso.

                      El caso nace con **rol y sin persona**: queda en la cola del rol hasta que
                      alguien lo toma. */}
                  <Card className="border border-brand-border rounded-sm" data-testid="ticket-propietario">
                    <CardHeader className="pb-2 border-b border-brand-border py-2">
                      <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em]">Propietario</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Rol</p>
                          <p className="mt-0.5">{rolLabel(teamRoles, selectedTicket?.rol_propietario) || <span className="text-zinc-400">sin rol</span>}</p>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Persona</p>
                          <p className="mt-0.5" data-testid="propietario-actual">
                            {selectedTicket?.propietario?.name || selectedTicket?.propietario?.id
                              || <span className="text-amber-700">Sin dueño — esta en la cola del rol</span>}
                          </p>
                        </div>
                      </div>

                      {/* El boton de tomar vive ahora en la fila de acciones de arriba, que
                          es la que se ve siempre — esta tarjeta esta adentro de una pestaña.
                          No se deja una copia aca: el estado y la prioridad ya aparecen dos
                          veces cada uno en esta pantalla, y duplicar una accion mas es
                          agregarle otra pregunta a quien la mira ("¿son el mismo boton?"). */}
                      {/* `teamMembersTicket.length` es la señal de que la lista de equipo ya
                          llego: `puedoTomar` sale de los roles que estan ahi, asi que sin esta
                          condicion el mensaje aparece un instante y despues se convierte en el
                          boton. Un cartel que dice "no podes" y se retracta solo es peor que
                          esperar medio segundo. */}
                      {!selectedTicket?.propietario?.id && !puedoTomar && teamMembersTicket.length > 0 && (
                        <p className="text-[11px] text-zinc-500" data-testid="tomar-no-es-tu-cola">
                          Este caso espera en la cola de{' '}
                          <strong>{rolLabel(teamRoles, selectedTicket?.rol_propietario) || 'otro rol'}</strong>.
                          Lo toma alguien de ese rol, o te lo pasa un supervisor.
                        </p>
                      )}

                      {!puedoTraspasar && selectedTicket?.propietario?.id && (
                        <p className="text-[11px] text-zinc-500" data-testid="traspaso-no-es-tuyo">
                          Este caso es de{' '}
                          <strong>{selectedTicket?.propietario?.name || selectedTicket?.propietario?.id}</strong>.
                          Pedile el traspaso, o pedile a un supervisor que te lo pase.
                        </p>
                      )}

                      {puedoTraspasar && (
                      <div className="flex flex-wrap items-end gap-2">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Traspasar a</p>
                          <select className="border border-brand-border rounded-sm h-8 px-2 text-xs bg-white min-w-[12rem] mt-0.5"
                            value={traspasoEmail} onChange={(e) => setTraspasoEmail(e.target.value)}
                            data-testid="traspaso-persona">
                            <option value="">(elegir persona)</option>
                            {(teamMembersTicket || []).map((m) => (
                              <option key={m.user_email} value={m.user_email}>{m.full_name || m.user_email}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Con el rol</p>
                          <select className="border border-brand-border rounded-sm h-8 px-2 text-xs bg-white mt-0.5"
                            value={traspasoRol} onChange={(e) => setTraspasoRol(e.target.value)}
                            data-testid="traspaso-rol">
                            <option value="">(mantener)</option>
                            {(teamRoles || []).map((r) => <option key={r.id} value={r.id}>{r.name || r.id}</option>)}
                          </select>
                        </div>
                        <Input className="h-8 text-xs rounded-sm flex-1 min-w-[10rem]" placeholder="Motivo (queda en el historial)"
                          value={traspasoMotivo} onChange={(e) => setTraspasoMotivo(e.target.value)} />
                        <Button size="sm" variant="outline" className="h-8 text-xs rounded-sm"
                          disabled={ownerBusy || !traspasoEmail} onClick={handleTraspasar} data-testid="btn-traspasar">
                          Traspasar
                        </Button>
                      </div>
                      )}

                      {(selectedTicket?.traspasos || []).length > 0 && (
                        <div className="border-t border-zinc-100 pt-2">
                          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Historial</p>
                          <ul className="space-y-0.5">
                            {(selectedTicket.traspasos || []).slice().reverse().map((t, i) => (
                              <li key={i} className="text-[11px] text-zinc-600">
                                <span className="text-zinc-400">{String(t.at).slice(0, 16).replace('T', ' ')}</span>
                                {' '}{t.de || '(cola)'} → <strong>{t.a || '(cola)'}</strong>
                                {t.a_rol && t.a_rol !== t.de_rol ? ` · rol ${t.a_rol}` : ''}
                                {t.motivo ? ` · ${t.motivo}` : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Timeline */}
                  {selectedTicket.timeline && selectedTicket.timeline.length > 0 && (
                    <Card className="border border-brand-border rounded-sm">
                      <CardHeader className="pb-2 border-b border-brand-border">
                        <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em]">Timeline</CardTitle>
                      </CardHeader>
                      <CardContent className="p-4">
                        <div className="space-y-2">
                          {selectedTicket.timeline.map((ev, i) => (
                            <div key={i} className="flex items-start gap-3 text-xs">
                              <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mt-1.5 shrink-0" />
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-zinc-400">{formatTime(ev.timestamp)}</span>
                                  <span className="text-[10px] text-zinc-400">{formatDate(ev.timestamp)}</span>
                                  <span className="font-medium text-zinc-700">{ev.action}</span>
                                </div>
                                {ev.actor_name && <span className="text-[10px] text-zinc-400">por {ev.actor_name}</span>}
                                {ev.action === 'management_note' && ev.details?.text && (
                                  <p className="text-[11px] text-zinc-700 mt-0.5 whitespace-pre-wrap max-w-md">{ev.details.text}</p>
                                )}
                                {ev.details?.message && <p className="text-[10px] text-zinc-500 mt-0.5 truncate max-w-md">{ev.details.message}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <WorkspacePanel
                    ticket={selectedTicket}
                    tenantId={tenantId}
                    capsuleId={selectedTicket?.capsule_id || queryCapsuleIds[0]}
                    refreshToken={workspaceRefreshToken}
                    onRequestConfirm={requestConfirm}
                  />
                </div>
              )}

              {activeTab === 'chat' && (
                <div className="h-full" data-testid="ticket-chat-panel">
                  <TicketChatPanel
                    ticket={selectedTicket}
                    tenantId={tenantId}
                    capsuleId={selectedTicket?.capsule_id || queryCapsuleIds[0]}
                    onArtifactCreated={refreshArtifacts}
                  />
                </div>
              )}

              {activeTab === 'conversation' && (
                <div className="h-full" data-testid="ticket-conversation-panel">
                  <ConversationThread
                    ticket={selectedTicket}
                    tenantId={tenantId}
                    capsuleId={selectedTicket?.capsule_id || queryCapsuleIds[0]}
                    onClose={() => setActiveTab('detail')}
                  />
                </div>
              )}

              {activeTab === 'reply' && (
                <div className="h-full" data-testid="ticket-reply-panel">
                  <TicketReplyPanel
                    ticket={selectedTicket}
                    tenantId={tenantId}
                    capsuleId={selectedTicket?.capsule_id || queryCapsuleIds[0]}
                  />
                </div>
              )}

              {activeTab === 'board' && (
                <TicketBoard
                  ticket={selectedTicket}
                  tenantId={tenantId}
                  capsuleId={selectedTicket?.capsule_id || queryCapsuleIds[0]}
                  onTicketRefresh={refreshSelectedTicketDetail}
                  highlightTaskId={highlightTaskId}
                  onHighlightDone={() => setHighlightTaskId(null)}
                />
              )}

              {/* **Arriba de la memoria del caso, no adentro.** «Qué le pasó a éste» y «a quién
                  más le pasó lo mismo» son dos preguntas distintas, y la segunda se hace al
                  empezar a trabajarlo — que es el momento en que sirve. */}
              {activeTab === 'memory' && (
                <div className="space-y-4">
                  <CasosParecidos
                    tenantId={tenantId}
                    capsuleId={selectedTicket?.capsule_id || queryCapsuleIds[0]}
                    ticketId={selectedTicket?.ticket_id}
                    // Abre el caso original en la misma pantalla. Si no está en la lista
                    // cargada —es viejo, o de otra cápsula— igual se pide por id.
                    onIrAlCaso={(id) => selectTicket(
                      tickets.find((t) => t.ticket_id === id)
                      || { ticket_id: id, capsule_id: selectedTicket?.capsule_id }
                    )}
                  />
                </div>
              )}

              {activeTab === 'memory' && (
                <MemoryEventPanel
                  tenantId={tenantId}
                  capsuleId={selectedTicket?.capsule_id || queryCapsuleIds[0]}
                  ticketId={selectedTicket?.ticket_id}
                  ticketStatus={selectedTicket?.status}
                />
              )}
            </div>
          </>
        )}
      </div>
      <ConfirmActionDialog
        open={confirmState.open}
        onOpenChange={(open) => {
          if (!open) setConfirmState({ open: false, title: '', description: '', onConfirm: null, confirmText: 'Confirmar' });
        }}
        title={confirmState.title}
        description={confirmState.description}
        confirmText={confirmState.confirmText}
        onConfirm={confirmState.onConfirm}
      />
      <ConfirmActionDialog
        open={purgeOpen}
        onOpenChange={(open) => {
          setPurgeOpen(open);
          if (!open) setPurgePhrase('');
        }}
        title="Vaciar todos los tickets"
        description={`Se eliminarán permanentemente todos los tickets del tenant (workspace, KB Tickets/{id}/, hilos vinculados). Escriba exactamente: ${PURGE_TICKETS_PHRASE}`}
        confirmText="Vaciar todo"
        confirmDisabled={purgePhrase.trim() !== PURGE_TICKETS_PHRASE || purgeLoading}
        onConfirm={handlePurgeAllTickets}
      >
        <Input
          value={purgePhrase}
          onChange={(e) => setPurgePhrase(e.target.value)}
          placeholder={PURGE_TICKETS_PHRASE}
          className="mt-2 text-xs rounded-sm"
          autoComplete="off"
        />
      </ConfirmActionDialog>
      <ConfirmActionDialog
        open={scrubOpen}
        onOpenChange={(open) => {
          setScrubOpen(open);
          if (!open) setScrubPhrase('');
        }}
        title="Limpiar huérfanos"
        description={`Borra solo docs cuyo ticket_id/conversation_id ya no existe. No toca tickets ni conversaciones vivos. Escriba: ${SCRUB_PHRASE}`}
        confirmText="Limpiar"
        confirmDisabled={scrubPhrase.trim() !== SCRUB_PHRASE || scrubLoading}
        onConfirm={handleOrphanScrub}
      >
        <Input
          value={scrubPhrase}
          onChange={(e) => setScrubPhrase(e.target.value)}
          placeholder={SCRUB_PHRASE}
          className="mt-2 text-xs rounded-sm"
          autoComplete="off"
        />
      </ConfirmActionDialog>
      <CambiarSolicitanteDialog
        open={cambiandoSolicitante}
        onOpenChange={setCambiandoSolicitante}
        actual={selectedTicket?.solicitante}
        onConfirm={handleCambiarSolicitante}
      />
    </div>
  );
}
